/**
 * Typed FastAPI client — wraps every backend endpoint the Pro app needs.
 *
 * The backend lives at `VITE_API_BASE_URL` (Cloud Run in prod, localhost
 * in dev). Every endpoint requires a Firebase ID token; this client
 * pulls one from `auth.currentUser` on each call, attaches it as a
 * Bearer header, and lets callers stay focused on the response shape.
 *
 * Endpoints wrapped:
 *   POST /report                  → blocking briefing generation
 *   POST /chat                    → SSE streaming briefing
 *   GET  /report/{t}/cover-pdf    → 3-page sector cover PDF
 *   GET  /report/{t}/full-pdf     → full PDF export
 *   GET  /report/{t}/magazine-pdf → magazine-style PDF
 *   GET  /portfolio               → user's saved holdings
 *   POST /portfolio               → save/replace holdings
 *   GET  /portfolio/prices        → live CMP for held tickers
 *   GET  /reports/history         → previous briefings (paginated)
 */
import { cachedSearch } from "./searchCache";
import { loadCompanies, searchCompanies } from "./companyIndex";
import { auth, appCheckHeader } from "@/firebase";
import type { PortfolioReviewResponse, SavedReviewCheckpoint } from "./portfolioReview";
import type { Intelligence, Notebook, MemoryHistory, WeeklyArchive } from "./portfolioIntelligence";
import type { FundOverlap, FundAlternatives } from "./fundOverlap";

export const getFundOverlap = (signal?: AbortSignal, results = false) => request<FundOverlap>(`/portfolio/review/fund-overlap${results ? "?results=true" : ""}`, { signal });
export const runFundOverlap = (scopeToken: string, signal?: AbortSignal) =>
  request<FundOverlap>("/portfolio/review/fund-overlap", { method: "POST", signal, json: { scope_token: scopeToken } });
export const compareFundAlternatives = (selected: string, scopeToken: string, signal?: AbortSignal) =>
  request<FundAlternatives>("/portfolio/review/fund-overlap/alternatives", { method: "POST", signal, json: { selected, scope_token: scopeToken } });

export const getPortfolioIntelligence = (signal?: AbortSignal) => request<Intelligence>("/portfolio/review/intelligence", { signal });
export const saveInvestmentMemory = (notebook: Notebook, signal?: AbortSignal) => request<Notebook>("/portfolio/review/memory", { method: "PUT", signal, json: notebook });
export const getInvestmentMemoryHistory = (signal?: AbortSignal) => request<MemoryHistory>("/portfolio/review/memory/history", { signal });
export const getWeeklyReviews = (signal?: AbortSignal) => request<WeeklyArchive>("/portfolio/review/weekly", { signal });

export const getPortfolioReview = (days = 90, signal?: AbortSignal) =>
  request<PortfolioReviewResponse>(`/portfolio/review?days=${days}`, { signal });
export const createPortfolioReviewPreview = (signal?: AbortSignal, scopeToken?: string) =>
  request<PortfolioReviewResponse>("/portfolio/review/preview", { method: "POST", signal, json: { scope_token: scopeToken } });
export const savePortfolioReviewCheckpoint = (checkpointToken: string, signal?: AbortSignal) =>
  request<SavedReviewCheckpoint>("/portfolio/review/checkpoint", { method: "POST", signal, json: { checkpoint_token: checkpointToken } });

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
  /\/$/,
  "",
);

if (!BASE_URL) {
  console.warn(
    "[api] VITE_API_BASE_URL is not set — API calls will fail. " +
      "Copy frontend-pro/.env.example to .env and fill it in.",
  );
}

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}


/** Structured 409 returned when a full-sleeve save would remove holdings. */
export interface HoldingsDropConflict {
  error: "WOULD_DROP_HOLDINGS";
  message: string;
  dropped: string[];
  n_stored: number;
  n_incoming: number;
  confirm_field: "confirm_drop";
}

/** Type guard for the backend detail object itself. */
export function isHoldingsDropConflictDetail(value: unknown): value is HoldingsDropConflict {
  if (!value || typeof value !== "object") return false;
  const detail = value as Partial<HoldingsDropConflict>;
  return detail.error === "WOULD_DROP_HOLDINGS" &&
    detail.confirm_field === "confirm_drop" &&
    typeof detail.message === "string" &&
    Array.isArray(detail.dropped) &&
    typeof detail.n_stored === "number" &&
    typeof detail.n_incoming === "number";
}

/** Extract a drop conflict from an ApiError or a raw FastAPI error body. */
export function getHoldingsDropConflict(value: unknown): HoldingsDropConflict | null {
  if (isHoldingsDropConflictDetail(value)) return value;
  const body = value instanceof ApiError ? value.body : value;
  if (isHoldingsDropConflictDetail(body)) return body;
  if (body && typeof body === "object") {
    const detail = (body as { detail?: unknown }).detail;
    if (isHoldingsDropConflictDetail(detail)) return detail;
  }
  return null;
}
/** Resolve `p` or give up after `ms`. The two token steps below run BEFORE
 *  fetch, so when either stalls (reCAPTCHA script blocked by an extension or a
 *  corporate proxy, a token refresh waiting on a dead socket) the button that
 *  called us simply never does anything: no request, no error, no log on the
 *  server. 2026-10-11: the owner's company lookup "did not work" and production
 *  had received no request at all. A bounded wait turns that into a visible
 *  error the screen can show. */
function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new ApiError(`${what} timed out`, 0)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

const ID_TOKEN_TIMEOUT_MS = 10_000;
const APP_CHECK_TIMEOUT_MS = 6_000;

async function authHeaders(): Promise<Record<string, string>> {
  const u = auth.currentUser;
  if (!u) throw new ApiError("Not signed in", 401);
  const token = await withTimeout(u.getIdToken(), ID_TOKEN_TIMEOUT_MS, "Sign-in check");
  // App Check is best-effort here: without the header the server answers 401
  // ("App Check verification failed"), which the screen can at least SHOW —
  // strictly better than a request that never leaves the browser.
  let ac: Record<string, string> = {};
  try {
    ac = await withTimeout(appCheckHeader(), APP_CHECK_TIMEOUT_MS, "App Check");
  } catch (e) {
    console.warn("[api] proceeding without App Check header:", e);
  }
  return { Authorization: `Bearer ${token}`, ...ac };
}

/** One sentence a person can act on when a lookup fails before or at the
 *  server. Exported for the two home screens; pure, so tests can import it. */
export function lookupFailureMessage(err: unknown): string {
  // Duck-typed on `status`, not `instanceof ApiError`: an error that crossed a
  // module boundary (or the test harness's stand-in class) must still be read.
  const e = err as { status?: unknown; message?: unknown } | null;
  if (e && typeof e === "object" && typeof e.status === "number") {
    const msg = typeof e.message === "string" ? e.message : "";
    if (e.status === 401 && /not signed in/i.test(msg))
      return "Your session has expired — sign in again to continue.";
    if (e.status === 401) return "We couldn't verify this browser (App Check). Reload the page and try again.";
    if (e.status === 0) return `${msg || "The request did not go through"}. Check your connection and try again.`;
    if (e.status === 429) return "Too many lookups in a short time — wait a moment and try again.";
    if (e.status >= 500) return "StockSense is briefly unavailable — try again in a few seconds.";
    return msg || "The lookup failed. Try again.";
  }
  if (err instanceof TypeError) return "Couldn't reach StockSense — check your connection and try again.";
  return "The lookup failed. Try again.";
}

/** Pull the human-readable message out of a FastAPI error body.
 *
 * The backend answers with `{detail: "..."}`, `{detail: {error, message}}` or
 * `{detail: [{msg, loc}]}` (pydantic) depending on the failure. Callers used to
 * each cast `detail` to a string, which renders "[object Object]" for two of
 * those three shapes — and for the object shape React throws outright when it
 * lands in JSX. Normalising once here means a caller can always show
 * `err.message` and get something a person can read.
 *
 * Returns undefined when the body carries no usable prose, so the caller falls
 * back to the technical `METHOD /path -> status` string.
 */
function serverMessage(payload: unknown): string | undefined {
  const detail = (payload as { detail?: unknown } | undefined)?.detail ?? payload;
  if (typeof detail === "string") return detail.trim() || undefined;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === "string" ? d : (d as { msg?: string })?.msg))
      .filter((m): m is string => !!m);
    return msgs.length ? msgs.join("; ") : undefined;
  }
  if (detail && typeof detail === "object") {
    const o = detail as { message?: unknown; error?: unknown; detail?: unknown };
    for (const v of [o.message, o.error, o.detail]) {
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  return undefined;
}

async function request<T>(
  path: string,
  init: Omit<RequestInit, "headers"> & {
    json?: unknown;
    headers?: Record<string, string>;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeaders()),
    Accept: "application/json",
    ...(init.headers ?? {}),
  };
  let body = init.body;
  if (init.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.json);
  }
  // A BODY IMPLIES POST. `fetch` throws "Request with GET/HEAD method cannot
  // have body" at construction, so a caller that passed `json` and forgot
  // `method` produced a button that never reached the network at all — no
  // request, no log, nothing to debug from. Four shipped call sites had exactly
  // that bug (build-client-portfolio, save-model-portfolio, watch-thesis,
  // generate-MITC). There is no legitimate GET-with-body, so defaulting here
  // makes the whole class impossible rather than fixing four instances.
  const method = init.method ?? (init.json !== undefined ? "POST" : undefined);
  const res = await fetch(`${BASE_URL}${path}`, { ...init, method, headers, body });
  if (!res.ok) {
    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      payload = await res.text().catch(() => undefined);
    }
    // F30: this said `init.method ?? "GET"`, the caller's ORIGINAL value —
    // so every request whose POST was inferred from `json` above reported
    // itself as "GET /x -> 500" in logs and in any surfaced message. We sent
    // people looking for a GET handler that was never called. Report what was
    // actually sent.
    throw new ApiError(
      serverMessage(payload) ?? `${method ?? "GET"} ${path} → ${res.status}`,
      res.status,
      payload,
    );
  }
  return (await res.json()) as T;
}

// ── AI portfolio analyst (retail) ───────────────────────────────────

export interface AiBudget {
  month: string;
  free_credits: number;
  spent_credits: number;
  free_left_credits: number;
  topup_credits: number;
  remaining_credits: number;
  /** "free" = one-time taster grant; "subscriber" = monthly allowance. */
  plan?: "free" | "subscriber";
  /** False when `free_credits` is a ONE-TIME grant that will not refill. */
  free_is_monthly?: boolean;
  credit_inr: number;
  /** What each AI action costs, straight from the server's CREDIT_COSTS.
   *  Never hardcode these client-side — they are re-tuned against measured
   *  COGS, and a stale copy quotes a price the wallet no longer charges. */
  credit_costs?: Record<string, number>;
}

/** Credits one AI action costs. `undefined` until the budget loads — callers
 *  must render nothing rather than guess, since guessing wrong about price is
 *  worse than staying quiet. */
export function creditCost(
  budget: AiBudget | null | undefined,
  feature: string,
): number | undefined {
  return budget?.credit_costs?.[feature];
}

export interface AssistantAnswer {
  available: boolean;
  text: string;
  budget: AiBudget;
}

export type AssistantTurn = { role: "user" | "assistant"; text: string };

export function getAiBudget(): Promise<AiBudget> {
  return request<AiBudget>("/assistant/budget");
}

/** Grounded Q&A over the user's own portfolio. Throws ApiError(402) with
 *  body {error:"AI_LIMIT_REACHED", budget} when the monthly AI budget is spent. */
export function assistantAsk(
  question: string,
  history: AssistantTurn[],
): Promise<AssistantAnswer> {
  return request<AssistantAnswer>("/assistant/ask", {
    method: "POST",
    json: { question, history },
  });
}

export interface WhyMoveInvestigation {
  headline?: string | null;
  primary_driver?: string | null;
  confidence?: number | null;
  other_drivers?: string | null;
  bottom_line?: string | null;
  verdict?: string | null;
  importance?: "low" | "medium" | "high" | null;
  pump_risk?: boolean | null;
  evidence?: { area: string; finding: string }[];
}

export interface WhyMoveResult {
  available: boolean;
  text?: string;               // fallback message when not available
  investigation?: WhyMoveInvestigation;
  budget?: AiBudget;
}

/** "Investigate this move" — Grok agentic search over filings/news + our data.
 *  Returns a structured investigation. Budget-metered (402 → AI_LIMIT_REACHED). */
export function whyDidItMove(
  ticker: string,
  name: string,
  move_pct?: number | null,
  price?: number | null,
): Promise<WhyMoveResult> {
  return request<WhyMoveResult>("/assistant/why-move", {
    method: "POST",
    json: { ticker, name, move_pct, price },
  });
}

/** Stock Deep Dive — descriptive research dossier (Grok web research + Haiku
 *  narration). `model` is a tier: "fast" (grok-4-fast) or "flagship" (grok-4.5). */
export interface DeepDiveResearch {
  business?: string | null;
  recent_developments?: string | null;
  earnings_highlights?: string | null;
  // Structured companions to the prose above (fact bullets, dated timeline,
  // headline metric cards). Additive — prose renders when these are absent.
  business_facts?: string[];
  developments?: { date: string; event: string }[];
  earnings_metrics?: { label: string; value: string; delta?: string }[];
  earnings_commentary?: string[];
  sources_checked?: string[];
  /** The most recent reported period the dossier is built on, e.g. "Q1 FY27". */
  latest_period?: string | null;
  /** Which dated development(s) coincided with the recent price move, or an
   *  explicit "no single disclosed event" — never speculation. */
  move_context?: string | null;
  // ── Deep agenda (flagship tier only; absent on a standard dossier) ──
  segment_trend?: { segment: string; fy: string; revenue?: string; share_pct?: string; growth?: string }[];
  competitive_position?: { peers: { name: string; metric?: string; value?: string; company_value?: string }[]; differentiation?: string };
  guidance_track?: { guided_on?: string; guidance: string; outcome?: string; outcome_period?: string }[];
  concall_themes?: { date?: string; theme: string; management_view?: string }[];
  capital_allocation?: string[];
  exceptional_items?: { period?: string; item: string; amount?: string }[];
  /** Adviser agenda (2026-10-13). */
  shareholding?: { quarter: string; promoter_pct?: string; fii_pct?: string; dii_pct?: string; public_pct?: string; pledged_pct?: string }[];
  disclosed_risks?: string[];
}
export interface DeepDiveResult {
  available: boolean;
  text?: string;                 // fallback message when unavailable
  ticker?: string;
  name?: string;
  research?: DeepDiveResearch | null;
  narrative?: string | null;     // Haiku descriptive read of our data
  snapshot?: StockSnapshot | null; // the deterministic data the chips/charts render from
  library_id?: string | null;    // saved copy in My research (2026-10-12)
  disclaimer?: string | null;
  budget?: AiBudget;
}
export function stockDeepDive(
  ticker: string,
  name: string,
  model: "fast" | "flagship" = "fast",
  position?: Record<string, unknown> | null,
): Promise<DeepDiveResult> {
  return request<DeepDiveResult>("/assistant/deep-dive", {
    method: "POST",
    json: { ticker, name, model, position },
  });
}

export interface HealthReportSection {
  title: string;
  flag: "good" | "watch" | "neutral";
  body: string;
}

export interface PortfolioHealthReport {
  headline: string;
  sections: HealthReportSection[];
  disclaimer: string;
}

export interface HealthReportResult {
  available: boolean;
  text?: string;                  // fallback message when not available
  report?: PortfolioHealthReport;
  budget?: AiBudget;
}

/** Portfolio Health Report — premium Fable synthesis over the user's own book.
 *  Descriptive + SEBI-linted. Budget-metered (402 → AI_LIMIT_REACHED). */
/** Portfolio Health Report. With no argument it reads the CALLER's own book;
 *  an adviser passes one of their own client ids to get the report on that
 *  client instead — which is the version an IFA actually wants. Ownership is
 *  enforced server-side (a client belonging to another adviser 404s). */
export function getPortfolioHealthReport(clientId?: string): Promise<HealthReportResult> {
  return request<HealthReportResult>("/assistant/health-report", {
    method: "POST",
    json: clientId ? { client_id: clientId } : {},
  });
}

// ── PayU subscription (retail Pro) ──────────────────────────────────

export interface PayuInitiateResponse {
  action_url: string;
  params: Record<string, string>;
}

/** Ask the backend to build a signed PayU request for `plan`. Returns the
 *  hosted-checkout URL + form fields; the caller POSTs them via payuSubmit
 *  (PayU rejects fetch/XHR — it requires a real top-level form submit). */
export async function payuInitiate(plan: "monthly" | "annual"): Promise<PayuInitiateResponse> {
  return request<PayuInitiateResponse>("/payu/initiate", { method: "POST", json: { plan } });
}

export type AiTopupSku =
  | "cr_99" | "cr_249" | "cr_499"            // retail ladder
  | "ai_15" | "ai_30" | "ai_50" | "ai_100";  // adviser ladder

/**
 * Which AI credit packs to offer this persona.
 *
 * The server enforces the same split (routers/payu._TOPUPS_FOR_ROLE) and 403s a
 * mismatch, so hardcoding one ladder in the UI — as three components used to —
 * means an adviser clicking "buy credits" gets a refusal rather than a
 * checkout. One list, derived from role, used everywhere.
 */
export function topupOptionsFor(role?: string): [AiTopupSku, string][] {
  return role === "ifa" || role === "mfd" || role === "ra"
    // "24 credits · ₹99", not "24 · ₹99": the owner's first look at the Deep
    // Dive wall (2026-10-11) read the bare numbers as prices, not as credits.
    ? [["ai_15", "15 credits · ₹75"], ["ai_30", "30 credits · ₹135"],
       ["ai_50", "50 credits · ₹210"], ["ai_100", "100 credits · ₹380"]]
    : [["cr_99", "24 credits · ₹99"], ["cr_249", "65 credits · ₹249"], ["cr_499", "145 credits · ₹499"]];
}

/** Start an AI top-up purchase (pay ₹X → +₹X AI budget) via PayU. */
export async function payuTopup(sku: AiTopupSku): Promise<PayuInitiateResponse> {
  return request<PayuInitiateResponse>("/payu/initiate", {
    method: "POST",
    json: { product: "topup", sku },
  });
}

/** sessionStorage key holding the credit balance as it stood the moment we
 *  handed the user to the payment gateway. See startAiTopup + F03. */
export const TOPUP_SNAPSHOT_KEY = "ss.topup.balanceBefore";

/** Kick off an AI top-up: initiate then form-POST to PayU.
 *
 * F03. The gateway returns the user to `?topup=1`, and the app used to read
 * that parameter as proof of purchase — "Credits added". It is not proof of
 * anything: the redirect is a browser navigation, it races the server-side
 * webhook that actually grants the credits, and the URL can simply be typed.
 * So we record the balance we are leaving with. On return, the banner compares
 * against it and reports what the WALLET says, not what the URL says.
 */
export async function startAiTopup(sku: AiTopupSku): Promise<void> {
  const r = await payuTopup(sku);
  try {
    const before = await getAiBudget();
    sessionStorage.setItem(
      TOPUP_SNAPSHOT_KEY,
      String(before.remaining_credits ?? ""),
    );
  } catch {
    // A wallet read failing must never block a payment the user asked for.
    // Without a snapshot the return banner falls back to "confirming", which
    // is honest — it just cannot quote the delta.
    try {
      sessionStorage.removeItem(TOPUP_SNAPSHOT_KEY);
    } catch {
      /* storage unavailable (private mode); nothing to clear */
    }
  }
  payuSubmit(r.action_url, r.params);
}

/** Submit PayU params as a top-level form POST → navigates the tab to PayU. */
export function payuSubmit(action_url: string, params: Record<string, string>): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action_url;
  for (const [k, v] of Object.entries(params)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

// ── /report ─────────────────────────────────────────────────────────

/** Structured synthesizer payload — mirrors tools/synthesis_schema.py.
 *  Every field is optional because the synthesizer may legitimately omit
 *  fields when the analysis doesn't support them. The editorial viewer
 *  renders defensively — any missing field collapses gracefully. */
export interface SynthesisJson {
  ticker: string;
  company_name: string;
  industry?: string;
  market_cap_category?: string;
  variant_view_md?: string;  // Deep Dive (research tier) only — adversarial "Variant View" markdown
  fiscal_year?: string;  // e.g. "FY26" — period labels bind to this, not "FY25"
  // Direction of travel — multi-year fundamental trajectory (the slope, not the level).
  trajectory?: {
    fy_range?: string;
    headline?: string;
    improving?: string[];
    deteriorating?: string[];
    metrics?: { label: string; direction: string }[];
  };
  // Macro backdrop + sector read-across — the global 'weather' framing the view.
  macro?: {
    regime?: string;
    headline?: string;
    tailwinds?: string[];
    headwinds?: string[];
    sector_read_across?: { note: string } | null;
  };
  verdict: {
    conviction_label: string;
    internal_label: string;
    upside_pct?: number | null;
    horizon_months?: number;
    risk_reward?: string | null;
  };
  quick_facts?: {
    market_price?: number;
    day_change_pct?: number;
    range_52w_low?: number;
    range_52w_high?: number;
    entry_low?: number;
    entry_high?: number;
    stop?: number;
    target_low?: number;
    target_high?: number;
  };
  kpis?: Array<{
    label: string;
    value: string;
    unit?: string;
    delta?: string;
    direction?: "up" | "down" | "flat";
  }>;
  executive_summary?: {
    paragraphs?: string[];
    thesis_pull_quote?: string;
  };
  scorecard?: {
    fundamentals_composite?: number;
    fundamentals_confidence?: "Low" | "Medium" | "High";
    fundamentals_caption?: string;
    valuation_composite?: number;
    valuation_confidence?: "Low" | "Medium" | "High";
    valuation_caption?: string;
    resilience_composite?: number;
    resilience_confidence?: "Low" | "Medium" | "High";
    resilience_caption?: string;
    fundamentals_breakdown?: Array<{
      label: string;
      score: number;
      weight?: number;
      value_label?: string;
      direction?: "weak" | "mid" | "strong";
    }>;
    valuation_breakdown?: Array<{
      label: string;
      score: number;
      weight?: number;
      value_label?: string;
      direction?: "weak" | "mid" | "strong";
    }>;
    resilience_breakdown?: Array<{
      label: string;
      score: number;
      weight?: number;
      value_label?: string;
      direction?: "weak" | "mid" | "strong";
    }>;
  };
  peer_comparison?: {
    rows?: Array<{
      name: string;
      ticker: string;
      is_self?: boolean;
      pe?: number;
      pb?: number;
      roe_pct?: number;
      ebitda_margin_pct?: number;
      revenue_growth_pct?: number;
      pat_growth_pct?: number;
    }>;
    callout?: string;
  };
  fundamentals?: {
    drivers?: Array<{ title: string; body: string }>;
    dupont_callout?: string;
  };
  technical?: {
    intro?: string;
    ladder?: Array<{
      label: string;
      sub?: string;
      price: number;
      delta_pct?: number;
      is_spot?: boolean;
    }>;
    momentum?: {
      rsi?: number;
      rsi_label?: string;
      volume_vs_20d_pct?: number;
      return_1m_pct?: number;
      return_3m_pct?: number;
      return_1y_pct?: number;
    };
    near_term_view?: {
      label: string;
      body: string;
      direction?: "bullish" | "bearish" | "neutral";
    };
    oversold_callout?: string;
  };
  risks?: Array<{
    severity: "high" | "med" | "low";
    severity_label: string;
    title: string;
    body: string;
    driver?: string;
    exposure?: string;
    mitigant?: string;
    watch?: string;
  }>;
  scenarios?: Array<{
    title: string;
    probability?: string;
    impact?: string;
    body: string;
  }>;
  investment_view?: {
    italic_lede?: string;
    rationale_paragraphs?: string[];
    action_plan?: Array<{ audience: string; action: string }>;
    key_watchout?: string;
    // Institutional-depth fields (emitted by the synthesizer; previously untyped
    // on the client so they never rendered). Suppress-to-empty.
    valuation_sensitivity?: { key_driver?: string; rows?: Array<{ assumption: string; implication: string }> };
    thesis_invalidation?: string[];
    catalyst_watch?: Array<{ event: string; why_it_matters: string; timing?: string }>;
  };
  company_snapshot?: {
    segments?: Array<{ title: string; pct?: number; description?: string }>;
    market_position?: string;
    catalysts?: string[];
    recent_developments?: Array<{ date: string; title: string; body: string }>;
  };
}

export interface ReportResponse {
  ticker: string;
  report: string;
  cached: boolean;
  generated_at: string;
  qc_status: "PASS" | "WARN" | "FAIL" | string;
  /** Structured payload — drives the editorial viewer. null when the
   *  synthesizer didn't emit a valid JSON block; viewer falls back to
   *  markdown rendering. */
  synthesis_json?: SynthesisJson | null;
  /** Which tier produced this report (executive|institutional); null for
   *  legacy/untiered reports. */
  tier?: BriefingTier | null;
  /** Which tiers have a report available for this ticker — lets the viewer
   *  offer "View" (sibling exists) vs "Generate"/upgrade (sibling missing). */
  available_tiers?: BriefingTier[];
}

export function generateReport(
  ticker: string,
  forceRefresh = false,
): Promise<ReportResponse> {
  return request<ReportResponse>("/report", {
    method: "POST",
    json: { ticker: ticker.toUpperCase().trim(), force_refresh: forceRefresh },
  });
}

/** Cache-only fetch — throws ApiError(404) if no saved briefing exists.
 *  NEVER triggers fresh generation; use this for the viewer / deep links
 *  where an accidental ~$0.32 regeneration would be unwelcome.
 *  `tier` returns that specific tier's report (viewer tier switch); omitted
 *  returns the most recent across tiers. The response's `available_tiers`
 *  lists which tiers exist for the ticker. */
export function fetchCachedReport(
  ticker: string,
  tier?: BriefingTier,
): Promise<ReportResponse> {
  const q = tier ? `?tier=${encodeURIComponent(tier)}` : "";
  return request<ReportResponse>(
    `/report/${encodeURIComponent(ticker.toUpperCase().trim())}/cached${q}`,
  );
}

// ── /chat/v2 (Cloud Tasks–backed async job — preferred over SSE) ────
//
// The SSE /chat endpoint hits Cloud Run / Fastly intermediate timeouts
// around the 10-min mark, killing the client connection mid-pipeline
// even though the backend keeps generating. /chat/v2 sidesteps that:
//   1. POST /chat/v2/start → enqueues a Cloud Tasks worker job, returns
//      {job_id} (or {job_id, cached:true} on same-day cache hit).
//   2. GET  /chat/v2/status/{job_id} → returns {status, progress, report}
//      every poll. status flips queued → running → complete | failed.
//   3. Worker runs server-side independent of client connection — the
//      browser can disconnect/reconnect freely; pipeline survives.

/** An in-flight report generation, for the Reports tab "Generating…" row. */
export interface ActiveReport {
  ticker: string;
  tier: string | null;
  job_id: string | null;
  started_at: string | null;
}
export function getActiveReports(): Promise<{ active: ActiveReport[] }> {
  return request<{ active: ActiveReport[] }>("/reports/active");
}

export interface BriefingJobStartResponse {
  job_id: string;
  /** True when a same-day cached report was returned without enqueuing.
   *  Caller can skip the polling loop and go straight to the viewer. */
  cached?: boolean;
  /** True when the user already has another in-flight job for this
   *  ticker — we return the existing job_id so two tabs converge. */
  deduped?: boolean;
}

export type BriefingJobStatusValue =
  | "queued"
  | "running"
  | "complete"
  | "failed"
  | string;

export interface BriefingJobStatus {
  job_id: string;
  ticker?: string;
  status: BriefingJobStatusValue;
  /** Backend updates this between agent stages. step is 1-7, matching
   *  the same 7-agent pipeline the SSE path emitted as text events. */
  progress?: { stage: string; step: number; of: number };
  /** Final markdown report — populated only when status === "complete". */
  report?: string | null;
  qc_status?: string | null;
  from_cache?: boolean;
  /** Error message — populated only when status === "failed". */
  error?: string | null;
}

/** Briefing tier: Executive Summary (Gemini, ₹200 / 2 credits) or Institutional
 *  (Claude, ₹500 / 5 credits). The IFA picks one per equity briefing. */
export type BriefingTier = "executive" | "institutional" | "research";

export function startBriefingJob(
  ticker: string,
  forceRefresh = false,
  tier: BriefingTier = "executive",
): Promise<BriefingJobStartResponse> {
  return request<BriefingJobStartResponse>("/chat/v2/start", {
    method: "POST",
    json: {
      ticker: ticker.toUpperCase().trim(),
      force_refresh: forceRefresh,
      tier,
    },
  });
}

export function getBriefingJobStatus(
  jobId: string,
): Promise<BriefingJobStatus> {
  return request<BriefingJobStatus>(
    `/chat/v2/status/${encodeURIComponent(jobId)}`,
  );
}

// ── /chat (SSE streaming — legacy, kept for the apex SPA) ───────────
export type ChatEvent =
  | { type: "session"; session_id: string }
  | { type: "cache"; hit: boolean }
  | { type: "text"; text: string }
  | {
      type: "progress";
      agent: number;
      phase: "start" | "done";
      pct: number;
      elapsed_ms: number;
    }
  | { type: "done"; ticker?: string; qc_status?: string }
  | { type: "error"; message: string }
  | { type: string; [key: string]: unknown };

export interface StreamReportOptions {
  ticker: string;
  forceRefresh?: boolean;
  userId?: string;
  signal?: AbortSignal;
  onEvent: (e: ChatEvent) => void;
}

/** Streams a briefing via the /chat SSE endpoint. Resolves when the
 *  stream closes; throws ApiError on non-2xx. Caller controls cancellation
 *  via AbortSignal. */
export async function streamReport(opts: StreamReportOptions): Promise<void> {
  const auth_h = await authHeaders();
  const headers: Record<string, string> = {
    ...auth_h,
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  const res = await fetch(`${BASE_URL}/chat`, {
    method: "POST",
    headers,
    signal: opts.signal,
    body: JSON.stringify({
      message: opts.ticker,
      user_id: opts.userId ?? "anonymous",
      force_refresh: opts.forceRefresh ?? false,
    }),
  });
  if (!res.ok || !res.body) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => undefined);
    }
    throw new ApiError(`POST /chat → ${res.status}`, res.status, body);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE event boundary is a blank line.
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      const dataLine = part
        .split("\n")
        .find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const raw = dataLine.slice(5).trim();
      if (!raw) continue;
      try {
        opts.onEvent(JSON.parse(raw) as ChatEvent);
      } catch {
        opts.onEvent({ type: "text", text: raw });
      }
    }
  }
}

// ── PDF endpoints ────────────────────────────────────────────────────
async function pdfBlob(path: string): Promise<Blob> {
  const headers = await authHeaders();
  const res = await fetch(`${BASE_URL}${path}`, { headers });
  if (!res.ok) throw new ApiError(`GET ${path} → ${res.status}`, res.status);
  return await res.blob();
}

export function fetchCoverPdf(ticker: string): Promise<Blob> {
  return pdfBlob(`/report/${encodeURIComponent(ticker)}/cover-pdf`);
}

export function fetchFullPdf(ticker: string): Promise<Blob> {
  return pdfBlob(`/report/${encodeURIComponent(ticker)}/full-pdf`);
}

export function fetchMagazinePdf(ticker: string, tier?: string | null): Promise<Blob> {
  const q = tier ? `?tier=${encodeURIComponent(tier)}` : "";
  return pdfBlob(`/report/${encodeURIComponent(ticker)}/magazine-pdf${q}`);
}

/** Opens a server-rendered PDF in a new browser tab. Uses an
 *  object-URL so the auth token never appears in the URL. */
export async function openPdfInNewTab(
  ticker: string,
  variant: "cover" | "full" | "magazine" = "magazine",
  tier?: string | null,
): Promise<void> {
  const blob =
    variant === "cover"
      ? await fetchCoverPdf(ticker)
      : variant === "full"
      ? await fetchFullPdf(ticker)
      : await fetchMagazinePdf(ticker, tier);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Revoke a beat later so the new tab has time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Open any already-fetched PDF blob in a new tab (object-URL, token-safe). */
export function openBlobInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Server-rendered client-ready PDF of an advisory note (visual deliverable +
 *  narrative). Advisor-only. Works for a fresh draft or a saved note — pass the
 *  reviewed text + the inputs_summary the panel already holds. */
export async function advisoryNotePdf(
  clientId: string,
  body: { final: string; inputs_summary?: unknown },
): Promise<Blob> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const res = await fetch(
    `${BASE_URL}/clients/${encodeURIComponent(clientId)}/advisory-note/pdf`,
    { method: "POST", headers, body: JSON.stringify(body) },
  );
  if (!res.ok) throw new ApiError(`advisory pdf → ${res.status}`, res.status);
  return await res.blob();
}

// ── /portfolio ───────────────────────────────────────────────────────
export interface Holding {
  ticker: string;
  qty: number;
  avg_buy_price: number;
  buy_date?: string;   // ISO; carried from the CSV so the ledger can reconstruct
  cost_basis?: string;
}

export interface PortfolioPayload {
  holdings: Holding[];
}

export interface HoldingsSaveOptions {
  /** Set only after the user reviews the holdings the replacement will remove. */
  confirm_drop?: boolean;
}

export interface PortfolioSavePayload extends PortfolioPayload, HoldingsSaveOptions {}

export interface HoldingsSaveResult {
  status: string;
  count: number;
}


export function getPortfolio(): Promise<PortfolioPayload> {
  return request<PortfolioPayload>("/portfolio");
}

export function savePortfolio(payload: PortfolioSavePayload): Promise<HoldingsSaveResult> {
  return request<HoldingsSaveResult>("/portfolio", {
    method: "POST",
    json: payload,
  });
}

export interface LivePrice {
  ticker: string;
  cmp: number;
  change_pct: number;
}

/**
 * Live CMP for the user's portfolio holdings.
 *
 * Backend `/portfolio/prices` returns the richer shape
 *   {as_of, holdings: [{ticker, qty, cmp, day_change_pct, ...}], totals}
 * (designed for the apex SPA which renders P&L server-side). For the Pro
 * app we only need the live-quote subset, so this wrapper extracts the
 * fields we care about and presents the simpler {prices: LivePrice[]}
 * shape downstream consumers expect.
 */
interface PortfolioPricesRaw {
  as_of?: string | null;
  holdings?: Array<{
    ticker: string;
    cmp?: number | null;
    day_change_pct?: number | null;
    change_pct?: number | null;
  }>;
  // Some older deploys may still return `prices` directly — accept both.
  prices?: LivePrice[];
}

export async function getPortfolioPrices(): Promise<{ prices: LivePrice[]; as_of: string | null }> {
  const r = await request<PortfolioPricesRaw>("/portfolio/prices");
  const as_of = r.as_of ?? null;
  // Already-simple shape: return as-is.
  if (Array.isArray(r.prices)) return { prices: r.prices, as_of };
  // Rich shape: extract ticker + cmp + change_pct from holdings.
  const prices: LivePrice[] = (r.holdings ?? [])
    .filter((h) => h && typeof h.ticker === "string")
    .map((h) => ({
      ticker: h.ticker,
      cmp: (h.cmp ?? 0) as number,
      change_pct: (h.change_pct ?? h.day_change_pct ?? 0) as number,
    }))
    // Drop entries where the backend couldn't resolve CMP (data_source=unavailable).
    .filter((p) => Number.isFinite(p.cmp) && p.cmp > 0);
  return { prices, as_of };
}

// ── /portfolio/mf + /clients/{id}/mf (MF holdings, valued vs latest NAV) ──
export interface MFHolding {
  scheme_name?: string;
  folio?: string;
  scheme_code: string;       // AMFI plan code
  isin?: string | null;      // permanent identity; survives scheme-code remaps
  units: number;
  invested?: number | null;  // cost; optional
  cost_source?: string | null; // "manual" | "statement" | null — provenance of `invested`
}

export interface ValuedMFHolding {
  scheme_code: string;
  isin?: string | null;
  scheme_name?: string | null;
  amc_name?: string | null;
  category?: string | null;
  asset_class?: string | null;
  units: number;
  nav: number;
  current_value: number;
  invested: number | null;
  gain: number | null;
  gain_pct: number | null;
  day_change_value: number | null;
  day_change_pct: number | null;
  // Dimensional quality scores — percentile within the fund's AMFI category
  // (higher = better returns / lower risk). Null when the fund has no metrics.
  returns_score?: number | null;
  risk_score?: number | null;
  cost_source?: string | null; // provenance of `invested` ("manual" | "statement")
}

export interface MFValuation {
  as_of: string | null;        // latest NAV date (EOD)
  holdings: ValuedMFHolding[];
  totals: {
    current_value?: number;
    invested?: number | null;
    gain?: number | null;
    gain_pct?: number | null;
    day_change_value?: number;
    asset_allocation?: Record<string, number>;
  };
  unresolved: string[];        // scheme_codes not found in the NAV feed
  // Same ETF held in BOTH sleeves (listed instrument + AMFI scheme) — counted
  // twice in the total. Advisory: reported, never auto-removed.
  etf_overlaps?: { ticker: string; equity_qty: number; scheme_code: string; scheme_name: string; mf_units: number; mf_value?: number | null }[];
  etf_overlap_notice?: string | null;
}

/** Individual user's own MF holdings, valued vs latest NAV. */
export function getPortfolioMF(): Promise<MFValuation> {
  return request<MFValuation>("/portfolio/mf");
}

export function saveMFPortfolio(
  mf_holdings: MFHolding[],
  options: HoldingsSaveOptions = {},
): Promise<HoldingsSaveResult> {
  return request("/portfolio/mf", { method: "POST", json: { mf_holdings, ...options } });
}

/** A client's MF holdings (IFA view), valued vs latest NAV. */
export function getClientMF(clientId: string): Promise<MFValuation> {
  return request<MFValuation>(`/clients/${encodeURIComponent(clientId)}/mf`);
}

/** Per-client MF valuation across the whole book in one NAV fetch — powers the
 *  client-book grid's live fund value + P&L (today/lifetime) for MFD/IFA books.
 *  P&L is on the costed subset; current_value covers all valued funds. */
export interface ClientMFValue {
  current_value: number | null;
  priced_cost: number;
  gain: number | null;
  gain_pct: number | null;
  day_change_value: number | null;
  n_funds: number;
  n_priced: number;
  n_costed: number;
  n_in_profit: number;
}
export interface ClientsMFValues {
  as_of: string | null;
  clients: Record<string, ClientMFValue>;
}
export function getClientsMFValues(): Promise<ClientsMFValues> {
  return request<ClientsMFValues>("/clients/mf-values");
}

export interface MFSchemeSearchResult {
  amfi_code: string;
  scheme_name: string;
  amc_name?: string | null;
  asset_class?: string | null;
  cagr_3y?: number | null;      // enriched when the metrics cache is warm
  ret_pctile?: number | null;   // 3Y return percentile within category
  risk_pctile?: number | null;  // risk-control percentile within category
}

/** Name search over the MF scheme catalog for the add-holding picker (>=3 chars). */
export function searchMFSchemes(
  q: string,
  limit = 200, // effectively unlimited for a typed search; the dropdown scrolls
): Promise<{ schemes: MFSchemeSearchResult[] }> {
  return request<{ schemes: MFSchemeSearchResult[] }>(
    `/mf/schemes/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  );
}

// ── Fund discoverer ──────────────────────────────────────────────────────────
export interface DiscoverFund {
  amfi_code: string;
  scheme_name: string;
  amc_name: string | null;
  cagr_1y: number | null;
  cagr_3y: number | null;
  cagr_5y: number | null;
  volatility: number | null;
  max_drawdown: number | null;
}
export interface DiscoverResult {
  category: string;
  category_label: string;
  sort_by: string;
  funds: DiscoverFund[];
  notes: string[];
}
export function discoverCategories(): Promise<{ categories: { key: string; label: string }[] }> {
  return request<{ categories: { key: string; label: string }[] }>("/mf/discover/categories");
}
export function discoverFunds(category: string, sort = "cagr_3y", limit = 20): Promise<DiscoverResult> {
  return request<DiscoverResult>(`/mf/discover?category=${encodeURIComponent(category)}&sort=${encodeURIComponent(sort)}&limit=${limit}`);
}

// ── Equity screener ──────────────────────────────────────────────────────────
export interface ScreenStock {
  ticker: string;
  company_name: string | null;
  sector: string | null;
  industry: string | null;
  market_cap_cr: number | null;
  roe_pct: number | null;
  roa_pct: number | null;
  profit_margin_pct: number | null;
  revenue_growth_pct: number | null;
  earnings_growth_pct: number | null;
  pe: number | null;
  pb: number | null;
  debt_equity: number | null;
  // Price action (null when the symbol isn't in the daily close feed yet)
  cmp: number | null;
  as_of: string | null;
  week52_high: number | null;
  week52_low: number | null;
  ret_1w_pct: number | null;
  ret_2w_pct: number | null;
  ret_1m_pct: number | null;
  ret_3m_pct: number | null;
  pos_52w_pct: number | null;
  off_high_pct: number | null;
}
export interface ScreenResult { sector: string | null; cap: string; sort_by: string; signals: string[]; stocks: ScreenStock[]; notes: string[] }
export interface ScreenMeta {
  sectors: string[];
  caps: { key: string; label: string }[];
  sorts: { key: string; label: string }[];
  signals: { key: string; label: string }[];
  numeric_filters: { key: string; label: string; unit: string }[];
}
// Multi-clause screen filters. Numeric keys are user-facing units (ROE in %,
// P/E in x, mkt cap in Cr); the backend scales them. signals are flag keys.
export interface ScreenFilters {
  sector?: string;
  cap?: string;
  signals?: string[];
  roe_min?: number; roa_min?: number; margin_min?: number;
  rev_growth_min?: number; eps_growth_min?: number;
  pe_max?: number; pe_min?: number; pb_max?: number; de_max?: number;
  mcap_min_cr?: number;
}
export function equityScreenMeta(): Promise<ScreenMeta> {
  return request<ScreenMeta>("/stocks/screen/meta");
}
export function screenEquity(filters: ScreenFilters = {}, sort = "return_on_equity", limit = 30): Promise<ScreenResult> {
  const q = new URLSearchParams({ cap: filters.cap || "all", sort, limit: String(limit) });
  if (filters.sector) q.set("sector", filters.sector);
  if (filters.signals?.length) q.set("signals", filters.signals.join(","));
  for (const k of ["roe_min", "roa_min", "margin_min", "rev_growth_min", "eps_growth_min", "pe_max", "pe_min", "pb_max", "de_max", "mcap_min_cr"] as const) {
    const v = filters[k];
    if (v != null && !Number.isNaN(v)) q.set(k, String(v));
  }
  return request<ScreenResult>(`/stocks/screen?${q.toString()}`);
}

export interface ParsedMFHolding {
  scheme_code: string;
  isin?: string | null;
  scheme_name?: string | null;
  units: number;
  invested?: number | null;
  confidence: string; // "isin" | "name"
  raw?: string;
}

export interface MFParseResult {
  holdings: ParsedMFHolding[];
  unresolved: Array<{ raw: string; units?: number | null; invested?: number | null; reason: string }>;
  total_rows: number;
}

// ── Your funds vs their index (retail active-vs-passive read on own holdings) ──
export interface PortfolioFundVsIndexRow {
  amfi_code: string; name: string; fund_3y: number | null; benchmark: string;
  index_3y: number | null; excess_3y: number | null; beat: boolean | null;
  category?: string; category_beat_rate?: number | null;
}
export interface PortfolioFundsVsIndex { available: boolean; n?: number; n_beat?: number; funds: PortfolioFundVsIndexRow[] }
/** Per held equity fund: did its 3-year NAV CAGR beat its category benchmark, with
 *  the category beat-rate context. The honest active-vs-passive read on your own funds. */
export function getPortfolioFundsVsIndex(): Promise<PortfolioFundsVsIndex> {
  return request<PortfolioFundsVsIndex>("/portfolio/mf/vs-index");
}
/** Advisor view: the same active-vs-passive read on a CLIENT's funds. */
export function getClientFundsVsIndex(clientId: string): Promise<PortfolioFundsVsIndex> {
  return request<PortfolioFundsVsIndex>(`/clients/${encodeURIComponent(clientId)}/mf/vs-index`);
}

// ── Regular-vs-Direct plan cost drag (the distributor cost you pay) ──────────
export interface CostDragFund {
  amfi_code: string; name: string; plan: string;
  held_cagr_3y: number | null; direct_cagr_3y: number | null;
  drag_pct: number | null; value: number | null; already_direct: boolean; direct_amfi_code?: string;
}
export interface PortfolioCostDrag {
  available: boolean; n_regular?: number; n_direct?: number;
  weighted_drag_pct?: number | null; annual_cost_inr?: number | null;
  value_regular?: number | null; funds: CostDragFund[];
}
/** For each Regular-plan fund held, the Direct plan's 3-year CAGR advantage — i.e.
 *  the distributor cost baked into the Regular plan, as an annual ₹ estimate. */
export function getPortfolioMfCostDrag(): Promise<PortfolioCostDrag> {
  return request<PortfolioCostDrag>("/portfolio/mf/cost-drag");
}

/** Parse a pasted MF-holdings CSV (fund name or ISIN, units, invested) into
 *  resolved holdings for preview before saving. */
export function parseMFHoldingsCsv(csvContent: string): Promise<MFParseResult> {
  return request<MFParseResult>("/mf/holdings/parse", {
    method: "POST",
    json: { csv_content: csvContent },
  });
}

/** Agentic parse of an UPLOADED MF holdings statement (CAS / Kuvera / MFCentral /
 *  AMC export, any layout). Claude Haiku extracts the holdings; resolution stays
 *  deterministic. `image_b64`+`media_type` route to VISION (a screenshot/photo of
 *  the statement). Same result shape as parseMFHoldingsCsv. */
export function parseMFStatement(
  payload: {
    file_content?: string; file_b64?: string; gcs_object?: string; filename?: string; image_b64?: string; media_type?: string;
    /** Opens a password-protected CAS. A CAMS/KFintech eCAS is ALWAYS locked —
     *  there is no unprotected export — so without this the most common MF
     *  statement in India cannot be uploaded at all. Used only to open the
     *  file; never stored, logged or echoed back. */
    password?: string;
  },
): Promise<MFParseResult> {
  return request<MFParseResult>("/mf/holdings/parse-file", { method: "POST", json: payload });
}

// ── Fund research (scorecard + NAV history + peers) — free NAV data ───────
export interface FundMetrics {
  cagr_1y: number | null;
  cagr_3y: number | null;
  cagr_5y: number | null;
  volatility: number | null;
  max_drawdown: number | null;
  downside_dev: number | null;
  n_days: number;
}

export interface FundScorecard {
  amfi_code?: string;
  category?: string | null;
  category_size?: number;
  metrics?: FundMetrics;
  returns?: { score: number | null };
  risk?: { score: number | null };
  confidence?: "high" | "medium" | "low" | string;
  benchmark?: { symbol: string; name: string };
  as_of?: string | null;
  /** Benchmark-relative analytics (Phase B) — capture ratios, beta,
   *  excess CAGR, Sharpe/Sortino vs the category-mapped index. */
  vs_benchmark?: {
    benchmark?: { symbol: string; name: string };
    sharpe_3y?: number | null;
    sortino_3y?: number | null;
    months?: number;
    up_capture?: number | null;
    down_capture?: number | null;
    beta?: number | null;
    benchmark_cagr_3y?: number | null;
    excess_cagr_3y?: number | null;
  };
  /** Returns-based style analysis — effective large/mid mix + 6m drift. */
  style?: {
    basis?: string[];
    large_pct?: number;
    mid_pct?: number;
    r2?: number | null;
    window_months?: number;
    drift_pts_6m?: number | null;
    note?: string;
  };
}

/** Deterministic returns/risk scorecard for one fund (percentile within its
 *  AMFI category). `{}` when the fund has no computed metrics yet. */
export function getFundScorecard(amfiCode: string): Promise<FundScorecard> {
  return request<FundScorecard>(`/mf/fund/${encodeURIComponent(amfiCode)}/scorecard`);
}

export interface NavPoint {
  date: string;
  nav: number;
}

export interface FundNavHistory {
  amfi_code: string;
  scheme_name: string | null;
  points: NavPoint[];
  as_of: string | null;
}

/** Daily NAV series for the research chart (default ~5y). */
export function getFundNavHistory(
  amfiCode: string,
  days = 1825,
): Promise<FundNavHistory> {
  return request<FundNavHistory>(
    `/mf/fund/${encodeURIComponent(amfiCode)}/nav-history?days=${days}`,
  );
}

export interface FundPeer {
  amfi_code: string;
  scheme_name: string | null;
  amc_name: string | null;
  cagr_1y: number | null;
  cagr_3y: number | null;
  cagr_5y: number | null;
  volatility: number | null;
  max_drawdown: number | null;
  returns_score: number | null;
  risk_score: number | null;
  rank: number;
  is_target: boolean;
}

export interface FundPeers {
  category: string | null;
  target_code: string;
  category_size: number;
  peers: FundPeer[];
}

/** Peer cohort (same AMFI category) with metrics + dimensional scores, ranked
 *  by 3y CAGR, target always included. For the fund-research comparison table. */
export function getFundPeers(amfiCode: string, limit = 15): Promise<FundPeers> {
  return request<FundPeers>(
    `/mf/fund/${encodeURIComponent(amfiCode)}/peers?limit=${limit}`,
  );
}

/** This fund's Regular-vs-Direct plan cost drag (the Direct sibling's 3y-CAGR
 *  advantage = the distributor commission). Descriptive, not a switch nudge. */
export interface FundCost {
  available: boolean;
  plan?: string | null;             // "Direct" | "Regular"
  already_direct?: boolean;
  held_cagr_3y?: number | null;
  direct_cagr_3y?: number | null;
  drag_pct?: number | null;         // Direct 3y CAGR − held 3y CAGR
  direct_amfi_code?: string | null;
  cost_per_lakh_inr?: number | null;
  name?: string | null;
}
export function getFundCost(amfiCode: string): Promise<FundCost> {
  return request<FundCost>(`/mf/fund/${encodeURIComponent(amfiCode)}/cost`);
}

// ── Equity price history (for the price chart) — free bhavcopy data ───────
export interface PricePoint {
  date: string;
  close: number;
}

export interface StockPriceHistory {
  symbol: string;
  points: PricePoint[];
  week52_high: number | null;
  week52_low: number | null;
  as_of: string | null;
}

/** Daily close series (+ latest 52-week high/low) for the equity price chart. */
export function getStockPriceHistory(
  ticker: string,
  days = 400,
): Promise<StockPriceHistory> {
  return request<StockPriceHistory>(
    `/stocks/${encodeURIComponent(ticker)}/price-history?days=${days}`,
  );
}

// ── Stock detail snapshot (deterministic, ₹0) ─────────────────────────────
export interface StockSnapshotSignal {
  label: string;
  detail: string;
  dir: "up" | "down" | "neutral";
}
export interface StockSnapshotPeer {
  ticker: string;
  company_name?: string | null;
  pe_ratio?: number | null;
  pb_ratio?: number | null;
  roe_pct?: number | null;
  ebitda_margin_pct?: number | null;
  revenue_growth_pct?: number | null;
}
export interface StockSnapshot {
  ticker: string;
  company: { name: string; sector?: string | null; industry?: string | null; market_cap_cat?: string | null };
  ratios: {
    pe: number | null; pb: number | null; ev_ebitda: number | null;
    roe_pct: number | null; roce_pct: number | null; debt_equity: number | null;
    ebitda_margin_pct: number | null; dividend_yield_pct: number | null;
    eps: number | null; latest_fy: string | null;
    /** Date the P/E and P/B are priced at; null => fiscal-year close. */
    as_of?: string | null;
    // Live-reconciled valuation (price/EPS, price/BVPS) — agrees with the live
    // price shown beside it; falls back to the FY pe/pb when unavailable.
    pe_live?: number | null; pb_live?: number | null;
  };
  technicals: {
    price: number | null; ma_20: number | null; ma_50: number | null; ma_200: number | null;
    rsi_14: number | null; week_52_high: number | null; week_52_low: number | null;
    volume_vs_avg_pct: number | null;
  };
  fundamentals: {
    series: { fy: string | null; revenue_cr: number | null; net_profit_cr: number | null; ebitda_margin_pct: number | null }[];
    revenue_cagr_pct: number | null;
    pat_cagr_pct: number | null;
  };
  signals: StockSnapshotSignal[];
  pros: string[];
  cons: string[];
  scorecard?: { industry?: string | null; fundamentals?: number | null; valuation?: number | null };
  peers: StockSnapshotPeer[];
  disclaimer: string;
}
export function getStockSnapshot(ticker: string): Promise<StockSnapshot> {
  return request<StockSnapshot>(`/stocks/${encodeURIComponent(ticker)}/snapshot`);
}

// ── "Across your book" — per-ticker exposure across the IFA's clients (₹0) ──
export interface StockHolder {
  id: string;
  name: string;
  value: number;
  weight_pct: number | null;
  over_concentrated: boolean;
}
export interface StockBookExposure {
  ticker: string;
  holders: StockHolder[];
  n_holders: number;
  book_value: number;
  book_equity_total: number;
  pct_of_book: number | null;
  over_concentrated_count: number;
  practice_risk: { drop_pct: number; value_at_risk: number; n_relationships: number } | null;
  as_of: string | null;
}
export function getStockBookExposure(ticker: string): Promise<StockBookExposure> {
  return request<StockBookExposure>(`/book/stocks/${encodeURIComponent(ticker)}/exposure`);
}

// ── Book monitor — proactive "what needs attention today" digest (₹0) ──────
export interface BookAlert {
  kind: "mover" | "low" | "drawdown" | "concentration"
      | "concentration_xray" | "mf_health" | "drift" | "watch";
  severity: "high" | "med" | "low";
  ticker?: string;
  client_id?: string;
  title: string;
  detail: string;
  value?: number;
  affected: { id: string; name: string; weight_pct?: number }[];
  id?: string;  // stable dismiss key
}


/** Dismiss (snooze_days omitted → permanent) or snooze an alert / tax candidate. */
export function dismissItem(id: string, snooze_days?: number): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/dismissals", { method: "POST", json: { id, snooze_days: snooze_days ?? null } });
}
export function restoreItem(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/dismissals/restore", { method: "POST", json: { id } });
}
/** Currently-active dismissed ids — to filter client-aggregated alerts (Alerts tab). */
export function getDismissed(): Promise<{ dismissed: string[] }> {
  return request<{ dismissed: string[] }>("/dismissals");
}

// ── Alert lifecycle state — when each alert first fired + what resolved today ──
export interface AlertStateEntry { first_seen: string; days: number; is_new: boolean; }
export interface AlertStateResponse {
  state: Record<string, AlertStateEntry>;
  resolved: { id: string; kind: string; title: string; first_seen: string }[];
}
/** Send the FULL computed alert set (pre dismiss-filter) → temporal metadata. */
export function getAlertState(
  alerts: { id?: string; kind: string; title: string }[],
): Promise<AlertStateResponse> {
  return request<AlertStateResponse>("/alerts/state", { method: "POST", json: { alerts } });
}
export interface BookMonitor {
  alerts: BookAlert[];
  n_clients: number;
  n_clients_with_holdings: number;
  as_of: string | null;
}
export function getBookMonitor(): Promise<BookMonitor> {
  return request<BookMonitor>("/book/monitor");
}

/** Same "needs attention" digest, scoped to the caller's OWN holdings. */
export function getPortfolioMonitor(): Promise<BookMonitor> {
  return request<BookMonitor>("/portfolio/monitor");
}

// ── Brinson performance attribution (₹0) ──────────────────────────────────
export interface AttributionSector {
  sector: string;
  port_weight: number; bench_weight: number;
  port_return: number; bench_return: number;
  allocation: number; selection: number; interaction: number; total: number;
}
export interface Attribution {
  available: boolean;
  reason?: string;
  allocation?: number; selection?: number; interaction?: number; total?: number;
  port_return?: number; bench_return?: number;
  by_sector?: AttributionSector[];
  window_days?: number; benchmark?: string; as_of?: string | null;
  /** F02: what this number was actually measured over. "Your return" here is
   *  NOT the header's trailing return — different coverage, and often a
   *  different window — so the panel states the difference instead of
   *  claiming agreement. */
  basis?: {
    holdings_total: number;
    holdings_measured: number;
    window_start?: string | null;
    window_end?: string | null;
    weighting: string;
    return_basis: string;
    decomposition_proxy?: boolean;
  };
  /** False when the proxy decomposition couldn't be scaled onto the actual excess
   *  — the headline is still the real return, but the per-sector effects won't sum
   *  to it, and the caption says so. */
  decomposition_reconciled?: boolean;
  decomposition_proxy?: boolean;
}
export function getPortfolioAttribution(days = 365): Promise<Attribution> {
  return request<Attribution>(`/portfolio/attribution?days=${days}`);
}

// ── Tax-loss harvesting (₹0) ──────────────────────────────────────────────
export interface HarvestLot {
  asset: string; key: string; name: string; qty: number;
  buy_value: number; current_value: number; unrealized: number;
  term: "ST" | "LT"; days_held: number; buy_date: string;
  id?: string;  // stable dismiss key
}
export interface TaxHarvest {
  fy: string;
  candidates: HarvestLot[];
  n_candidates: number;
  harvestable_st_loss: number;
  harvestable_lt_loss: number;
  total_harvestable_loss: number;
  realized_st_gain_fy: number;
  realized_lt_gain_fy: number;
  offset_potential: number;
  notes: string[];
}
export function getPortfolioTaxHarvest(): Promise<TaxHarvest> {
  return request<TaxHarvest>("/portfolio/tax-harvest");
}
export function getClientTaxHarvest(clientId: string): Promise<TaxHarvest> {
  return request<TaxHarvest>(`/clients/${encodeURIComponent(clientId)}/tax-harvest`);
}

/** AI tax explainer — Haiku plain-English read over the deterministic tax-harvest
 *  data (own portfolio; retail only). Budget-metered (1 credit). */
export interface TaxExplainer {
  available: boolean;
  text?: string;                 // fallback message when unavailable
  narrative?: string;
  data?: {
    fy?: string; realized_st_gain_fy?: number; realized_lt_gain_fy?: number;
    harvestable_st_loss?: number; harvestable_lt_loss?: number;
    total_harvestable_loss?: number; offset_potential?: number; n_candidates?: number;
  };
  budget?: AiBudget;
}
export function explainTax(): Promise<TaxExplainer> {
  return request<TaxExplainer>("/assistant/tax-explainer", { method: "POST", json: {} });
}

// ── Per-holding return & risk contribution (₹0) ───────────────────────────
export interface Contribution {
  available: boolean;
  reason?: string;
  as_of?: string | null;
  window_days?: number;
  return_contribution?: { key: string; name: string; weight: number; return: number; contribution: number }[];
  risk_contribution?: {
    available: boolean;
    portfolio_vol_ann_pct?: number;
    rows?: { key: string; name: string; weight: number; risk_contribution_pct_ann: number; share_of_risk: number }[];
  };
}
export function getPortfolioContribution(days = 365): Promise<Contribution> {
  return request<Contribution>(`/portfolio/contribution?days=${days}`);
}
export function getClientContribution(clientId: string, days = 365): Promise<Contribution> {
  return request<Contribution>(`/clients/${encodeURIComponent(clientId)}/contribution?days=${days}`);
}
export function getClientAttribution(clientId: string, days = 365): Promise<Attribution> {
  return request<Attribution>(`/clients/${encodeURIComponent(clientId)}/attribution?days=${days}`);
}

// ── Expectations X-Ray (₹0) ───────────────────────────────────────────────
/** The assumptions behind every implied-growth number. Returned so they can be
 *  DISPLAYED — an implied rate is meaningless without the discount rate,
 *  horizon and exit multiple that produced it. */
export interface ExpectationsAssumptions {
  cost_of_equity_pct: number;
  horizon_years: number;
  exit_multiple: number;
  /** Delivered CAGR is clamped at this ceiling; the raw rate is kept per holding. */
  delivered_growth_capped_at_pct: number;
}
export interface ExpectationsHolding {
  ticker: string;
  value: number;
  implied_growth_pct: number | null;
  /** Clamped at `delivered_growth_capped_at_pct`. */
  delivered_growth_pct: number | null;
  /** The uncapped CAGR — must be shown wherever `delivered_clamped` is true. */
  delivered_growth_raw_pct: number | null;
  delivered_clamped: boolean;
  gap_pp: number | null;
  history_years: number;
  fiscal_year?: number | string | null;
  /** Display threshold only (gap ≥ 5pp) — carries no judgement. */
  notable_gap: boolean;
}
export interface ExpectationsExcluded { ticker: string; value: number; reason: string }
export interface PortfolioExpectations {
  available: boolean;
  /** Present only when `available` is false — a quiet reason, not an error. */
  reason?: string;
  as_of?: string | null;
  assumptions?: ExpectationsAssumptions;
  portfolio?: {
    implied_growth_pct: number | null;
    delivered_growth_pct: number | null;
    gap_pp: number | null;
    basis: string;
  };
  holdings?: ExpectationsHolding[];
  /** Positions that could not be priced, WITH the reason. Never hidden. */
  excluded?: ExpectationsExcluded[];
  coverage?: { priced: number; excluded: number; value_covered_pct: number | null };
  disclaimer?: string;
}
/** What compounded earnings growth each holding's CURRENT price already embeds,
 *  against what it has delivered, value-weighted into one portfolio figure.
 *  Deterministic (₹0). Descriptive — no forecast, target or verdict. */
export function getPortfolioExpectations(): Promise<PortfolioExpectations> {
  return request<PortfolioExpectations>("/portfolio/expectations");
}

// ── Agentic MF research note (Sonnet+web → synthesizer → QC) ──────────────
/** Explicit RIA-only recommendation. Present only when the server's MF_RECO_ENABLED
 *  flag is on AND the caller is an IFA (RIA) — null/absent otherwise. */
export interface FundRecommendation {
  action: "Buy" | "Accumulate" | "Avoid" | string;
  conviction: "High" | "Moderate" | "Low" | string;
  thesis: string;
  reasons: string[];
  suitability: string;
  risks_to_view: string[];
  /** Verdict is deterministic (rubric); these expose the basis for auditability. */
  net_score?: number;
  coverage?: number;
  signals_fired?: number;
  /** Signals pre-coded but awaiting data (e.g. "GFD: expense ratio") — the GFD upside. */
  signals_pending?: string[];
  data_as_of?: string;
  disclosure: string;
  engine?: string;
}

export interface FundResearchNote {
  amfi_code: string;
  scheme_name: string;
  note_md: string;
  qc: { verdict: "PASS" | "WARN" | "FAIL" | string; notes: string };
  recommendation?: FundRecommendation | null;
  cached: boolean;
  generated_at?: string;
  cost_usd?: number;
}

/** Generate (or return today's cached) AI research note for a fund. A FRESH run
 *  costs ~₹40-70 of Anthropic; same-day repeat views are free (server-cached). */
export function generateFundResearch(amfiCode: string): Promise<FundResearchNote> {
  return request<FundResearchNote>(
    `/mf/fund/${encodeURIComponent(amfiCode)}/research`,
    { method: "POST", json: {} },
  );
}

// ── Benchmark index history (NIFTY) — for the "vs NIFTY" chart overlay ────
export interface IndexPoint {
  date: string;
  close: number;
}

export interface IndexHistory {
  index_symbol: string;
  points: IndexPoint[];
  as_of: string | null;
}

/** Daily close series for a benchmark index (NIFTY50 / NIFTYBANK). */
export function getIndexHistory(
  symbol: string,
  days = 400,
): Promise<IndexHistory> {
  return request<IndexHistory>(
    `/market/index/${encodeURIComponent(symbol)}/history?days=${days}`,
  );
}

// ── /stocks/search + /equity/holdings/parse (Equities add + bulk import) ──
export interface StockSearchResult {
  ticker: string;
  company_name: string;
  isin?: string | null;
}

export function searchStocks(
  q: string,
  limit = 20,
): Promise<{ stocks: StockSearchResult[] }> {
  // 1. The static company list on the CDN (lib/companyIndex), searched in the
  //    browser with the server's own ranking: instant, works with the backend
  //    cold or down. Waited for at most 1.5 s on the very first keystroke.
  // 2. No local match (a listing since the last export) → the server, through
  //    the browser cache (lib/searchCache). Every search box goes through here.
  const local = Promise.race([
    loadCompanies(),
    new Promise<null>((r) => setTimeout(() => r(null), 1500)),
  ]).catch(() => null);
  return local.then((rows) => {
    const hits = rows ? searchCompanies(rows, q, limit) : [];
    if (hits.length) return { stocks: hits as StockSearchResult[] };
    return cachedSearch(q, limit, (qq, ll) =>
      request<{ stocks: StockSearchResult[] }>(
        `/stocks/search?q=${encodeURIComponent(qq)}&limit=${ll}`,
      ).then((r) => r.stocks || []),
    ).then((stocks) => ({ stocks }));
  });
}

export interface ParsedEquityHolding {
  ticker: string;
  company_name?: string | null;
  qty: number;
  avg_buy_price?: number | null;
  confidence: string;
  raw?: string;
}

export interface EquityParseResult {
  holdings: ParsedEquityHolding[];
  unresolved: Array<{ raw: string; qty?: number | null; avg_buy_price?: number | null; reason: string }>;
  total_rows: number;
}

export function parseEquityHoldingsCsv(csvContent: string): Promise<EquityParseResult> {
  return request<EquityParseResult>("/equity/holdings/parse", {
    method: "POST",
    json: { csv_content: csvContent },
  });
}

/** Agentic parse of an UPLOADED equity holdings statement (broker export / CAS,
 *  any layout). Claude Haiku extracts the holdings; resolution stays deterministic.
 *  `image_b64`+`media_type` route to VISION (a broker-app screenshot/photo). */
export function parseEquityStatement(
  payload: {
    file_content?: string; file_b64?: string; gcs_object?: string; filename?: string; image_b64?: string; media_type?: string;
    /** Opens a password-protected CAS — see parseMFStatement. */
    password?: string;
  },
): Promise<EquityParseResult> {
  return request<EquityParseResult>("/equity/holdings/parse-file", { method: "POST", json: payload });
}

/**
 * Run an upload parse, and if the file turns out to be a locked CAS, ask for
 * the password once and retry the SAME file.
 *
 * Shared rather than inlined at each call site: the MF and equity uploads had
 * already drifted apart once — the backend flagged `needs_password` on both and
 * only the MF screens knew to act on it, so an equity CAS showed the user a
 * dead-end message telling them to re-upload with a password and no way to
 * supply one. Four upload surfaces, one behaviour.
 *
 * The password is passed straight through to the parse call and never stored.
 */
export async function parseWithCasPassword<
  T extends { unresolved?: unknown[] },
  P extends { password?: string },
>(
  parse: (payload: P) => Promise<T>,
  payload: P,
): Promise<T> {
  const first = await parse(payload);
  const locked = (first.unresolved as { needs_password?: boolean }[] | undefined)
    ?.some((u) => u?.needs_password);
  if (!locked) return first;
  const pw = window.prompt(
    "This statement is password-protected.\n\n" +
    "Enter the password you set when requesting it — for a CAMS/KFintech or " +
    "CDSL eCAS this is usually your PAN in capitals.");
  if (!pw) return first;
  return parse({ ...payload, password: pw });
}

/** Auto-detecting statement parse. Reads MF Central CAS workbooks, broker
 *  holdings exports (.xlsx) and CDSL eCAS PDFs, working out which is which from
 *  the file CONTENT — a statement downloaded from Gmail arrives with a generated
 *  filename, so the extension proves nothing.
 *
 *  Returns a PREVIEW; nothing is saved until the caller writes it through the
 *  normal holdings endpoints. `pan` is only used to open a password-protected
 *  eCAS and is never stored server-side. */
export interface StatementParseResult {
  ok: boolean;
  kind?: "mfcentral_cas" | "broker_holdings" | "cdsl_ecas" | "legacy_xls" | "unknown" | string;
  error?: string;
  message?: string;
  advisory?: string;
  importable?: boolean;
  holdings?: { ticker?: string; qty?: number; avg_buy_price?: number; company_name?: string }[];
  funds?: { scheme_code?: string; scheme_name?: string; isin?: string | null; folio?: string | null; units?: number; invested?: number }[];
  transactions?: { scheme_code?: string; isin?: string | null; folio?: string | null; scheme_name: string; side: "BUY" | "SELL"; date: string; qty: number; price: number; kind?: string }[];
  unresolved?: { raw?: string; reason?: string }[];
  needs_attention?: { scheme_name: string; statement_units: number; reason: string }[];
  coverage?: { parsed_value?: number; stated_total?: number; coverage_pct?: number };
  warnings?: string[];
  counts?: Record<string, number>;
}

/** Same-index tracker peers for a PASSIVE holding — "is the tracker I hold the
 *  right one?". Returns {available:false} for anything that isn't a recognised
 *  index fund/ETF; this surface says nothing about active funds. Peer-RELATIVE
 *  only: fund NAVs are total-return while our index series is price-return, so
 *  the absolute "vs index" number is biased and is not exposed. */
export interface TrackerPeer {
  amfi_code: string; name: string; amc?: string; vehicle?: string;
  cagr_3y?: number; tracking_diff_3y?: number; is_yours?: boolean;
}
export interface TrackerPeers {
  available: boolean;
  index?: string; rank?: number; of?: number;
  yours?: TrackerPeer; tightest?: TrackerPeer;
  gap_vs_tightest_pp?: number; vs_peer_median_pp?: number;
  peers?: TrackerPeer[]; caveat?: string;
}
/** Email digest subscriptions. Opt-in; everything defaults OFF. */
export interface DigestPrefs { daily?: boolean; weekly?: boolean; monthly?: boolean }
export function getDigestPrefs(): Promise<{ prefs: DigestPrefs }> {
  return request<{ prefs: DigestPrefs }>("/digest/prefs");
}
export function setDigestPrefs(prefs: DigestPrefs): Promise<{ prefs: DigestPrefs }> {
  return request<{ prefs: DigestPrefs }>("/digest/prefs", { method: "POST", json: prefs });
}

export function getTrackerPeers(amfiCode: string): Promise<TrackerPeers> {
  return request<TrackerPeers>(`/mf/tracker-peers?code=${encodeURIComponent(amfiCode)}`);
}

/** Mint a short-lived URL for uploading one statement straight to GCS.
 *  Used only for files too big for the base64/JSON path — see lib/upload.ts. */
export interface SignedUpload {
  url: string;
  object_name: string;
  expires_in_s: number;
  content_type: string;
}
export function getSignedUploadUrl(payload: {
  filename: string;
  size_bytes: number;
  content_type?: string;
}): Promise<SignedUpload> {
  return request<SignedUpload>("/uploads/signed-url", { method: "POST", json: payload });
}

export function parseStatement(
  payload: {
    /** Inline base64 — the small-file path. */
    file_b64?: string;
    /** GCS object name from /uploads/signed-url — the large-file path.
     *  Exactly one of these two is set; see lib/upload.ts. */
    gcs_object?: string;
    filename?: string;
    pan?: string;
  },
): Promise<StatementParseResult> {
  return request<StatementParseResult>("/portfolio/statement/parse", { method: "POST", json: payload });
}

// ── /reports/history ─────────────────────────────────────────────────
/** Shape returned by GET /reports/history — fields are camelCase per
 *  Python doc-dict convention from save_report_to_user_history(). */
export function discardStatementUpload(gcs_object: string): Promise<{ ok: boolean }> {
  return request("/portfolio/statement/discard", { method: "POST", json: { gcs_object } });
}

export interface ReportHistoryItem {
  id: string;
  ticker: string;
  recommendation?: string;
  qc_status?: string;
  generatedAt?: string;
  tier?: string; // "executive" | "institutional" — derived from the cache key
  // Older docs may carry additional legacy fields; we ignore them.
}

export function getReportHistory(
  limit = 20,
): Promise<{ reports: ReportHistoryItem[] }> {
  return request<{ reports: ReportHistoryItem[] }>(
    `/reports/history?limit=${limit}`,
  );
}

// ── /reports/mf-history ──────────────────────────────────────────────
/** A past mutual-fund research note (one per fund, latest wins). Reopened
 *  client-side via the fund research modal, so it carries the amfi_code. */
export interface MfReportHistoryItem {
  id: string;
  amfi_code: string;
  scheme_name?: string;
  qc_status?: string;
  has_reco?: boolean;
  generatedAt?: string;
}

export function getMfReportHistory(): Promise<{ reports: MfReportHistoryItem[] }> {
  return request<{ reports: MfReportHistoryItem[] }>(`/reports/mf-history`);
}

// ── Model-portfolio factsheet (SEBI RA model-portfolio guidelines) ───
export interface Factsheet {
  portfolio_type: string;
  launch_date: string;
  investment_horizon: string;
  benchmark: string;
  review_cadence: string;
  methodology: string;
  constituents: string;
  n_constituents: number;
  risk_disclosures: string[];
}

// ── /funds/basket — MFD model-portfolio recommender ──────────────────
export interface BasketFund {
  amfi_code: string;
  name: string;
  amc: string;
  sleeve: string;
  category: string;
  benchmark?: string;
  weight: number;
  amount_inr: number;
  cagr_3y: number;
  excess_3y: number | null;
  beat_index: boolean;
  volatility: number;
  max_drawdown: number;
  aum_cr: number | null;
  returns_pctile: number;
  risk_pctile: number;
  rolling?: { beat_pct: number; median_excess: number; n: number } | null;
}
export interface BasketNarration {
  summary?: string;
  fund_rationales?: { amfi_code: string; why: string }[];
  suitability?: string;
  notes?: string[];
}
export interface FundBasket {
  available: boolean;
  amount_inr: number;
  risk: string;
  horizon?: string | null;
  equity_pct: number;
  debt_pct: number;
  debt_note: string;
  debt_amount_inr: number;
  n_funds: number;
  funds: BasketFund[];
  caveats: string[];
  narration?: BasketNarration;
  factsheet?: Factsheet;
  disclaimer?: string;
  cached?: boolean;
}

/** Build an MFD model-portfolio for an amount + client risk appetite (MFD/IFA,
 *  behind the server MF_BASKET_ENABLED flag). Deterministic picks + Gemini "why". */
export function buildFundBasket(
  amount: number,
  risk: "conservative" | "moderate" | "aggressive",
  horizon?: string,
): Promise<FundBasket> {
  return request<FundBasket>(`/funds/basket`, {
    method: "POST",
    json: { amount, risk, horizon: horizon || null },
  });
}

// ── /equity/basket — IFA equity model-watchlist (UID-gated) ──────────
export interface BasketStock {
  ticker: string;
  name: string;
  sector: string;
  industry: string;
  cap: string;
  weight: number;
  amount_inr: number;
  fundamentals_score: number | null;
  valuation_score: number | null;
  momentum_score?: number | null;
  trend_score?: number | null;
  composite: number | null;
  roe: number | null;
  pe: number | null;
  pb: number | null;
  cmp: number | null;
  is_psu?: boolean;
  confidence?: "high" | "medium" | "low";
  data_flags?: string[];
  ladder?: { entry: number; stop: number; review: number; basis?: string } | null;
  _swapped?: boolean;   // frontend: adviser swapped this name in
}
export interface AltStock {
  ticker: string; name: string; sector: string; cap: string;
  fundamentals_score: number | null; valuation_score: number | null;
  momentum_score?: number | null; trend_score?: number | null; composite: number | null;
  roe: number | null; pe: number | null; cmp: number | null; is_psu?: boolean;
  ladder?: { entry: number; stop: number; review: number; basis?: string } | null;
}
export interface EquityBasketNarration {
  summary?: string;
  stock_rationales?: { ticker: string; why: string }[];
  suitability?: string;
  notes?: string[];
}
export interface EquityBasket {
  available: boolean;
  amount_inr: number;
  risk: string;
  horizon?: string | null;
  n_stocks: number;
  n_sectors: number;
  psu_pct?: number;
  excluded_low_confidence?: number;
  n_flagged?: number;
  cap_mix: Record<string, number>;
  stocks: BasketStock[];
  alternates?: Record<string, AltStock[]>;
  caveats: string[];
  narration?: EquityBasketNarration;
  factsheet?: Factsheet;
  disclaimer?: string;
  cached?: boolean;
  client_notes?: string[];
  client_name?: string;
}
/** A model portfolio for one client's new money — complements their book (excludes held
 *  names, defaults to their risk profile, steers away from their concentrated sectors). */
export function buildClientPortfolio(clientId: string, amount: number, risk?: string, horizon?: string): Promise<EquityBasket> {
  return request<EquityBasket>(`/clients/${encodeURIComponent(clientId)}/portfolio`, {
    method: "POST",
    json: { amount, risk: risk || null, horizon: horizon || null },
  });
}

/** Whether the equity basket is available to this adviser (server allowlist). */
export function getEquityBasketAvailable(): Promise<{ available: boolean }> {
  return request<{ available: boolean }>(`/equity/basket/available`);
}

/** Build the IFA equity model-watchlist for an amount + client risk appetite. */
export function buildEquityBasket(
  amount: number,
  risk: "conservative" | "moderate" | "aggressive",
  horizon?: string,
  caps?: { Large: number; Mid: number; Small: number } | null,
): Promise<EquityBasket> {
  return request<EquityBasket>(`/equity/basket`, {
    method: "POST",
    json: { amount, risk, horizon: horizon || null, caps: caps || null },
  });
}

// Implied expectations — reverse-DCF + scenario bands. Conditional arithmetic the
// adviser drives, NOT a forecast or a price target.
export interface ImpliedScenario {
  case: string;
  growth_pct: number;
  exit_multiple: number;
  implied_price: number;
  vs_cmp_pct: number;
}
export interface ImpliedAssumptions {
  cost_of_equity_pct: number;
  horizon_years: number;
  exit_multiple: number;
}
export interface ImpliedBands {
  available: boolean;
  scenarios: ImpliedScenario[];
  assumptions: ImpliedAssumptions;
  base_growth_pct?: number;
  implied_growth_pct?: number | null;
}
export interface ImpliedExpectations {
  available: boolean;
  reason?: string;
  implied_growth_pct: number | null;
  historical_pat_cagr_pct: number | null;
  base_growth_pct: number | null;
  base_growth_capped: boolean;
  expectation_gap_pp: number | null;
  bands: ImpliedBands;
  assumptions: ImpliedAssumptions;
  reading: string | null;
  caveats: string[];
  inputs?: {
    ticker: string; name: string; market_cap_cr: number; cmp: number;
    pe: number | null; price_as_of: string | null; pat: number; pat_history: number[];
  };
}
/** What today's price already assumes for one stock (cached, ₹0). */
export function getStockImplied(ticker: string): Promise<ImpliedExpectations> {
  return request<ImpliedExpectations>(`/stock/${encodeURIComponent(ticker)}/implied`);
}
/** Re-run the bands against adviser-set assumptions — pure math, no DB hit. */
export function recomputeScenario(body: {
  market_cap_cr: number; pat: number; cmp: number;
  growth_pct?: number | null; cost_of_equity_pct?: number;
  horizon_years?: number; exit_multiple?: number;
}): Promise<ImpliedBands> {
  return request<ImpliedBands>(`/implied/scenario`, { method: "POST", json: body });
}

// Daily 5 — adviser-only daily attention screen (deterministic engine + Gemini narration).
export interface DailyPickSignals {
  technical: number | null;
  event: number | null;
  volume: number | null;
  qtrend: number | null;
}
export interface DailyPick {
  ticker: string;
  name: string;
  sector: string;
  industry: string;
  cap: string;
  cmp: number | null;
  daily_score: number | null;
  signals: DailyPickSignals;
  reasons: string[];
  fundamentals_score: number | null;
  valuation_score: number | null;
  roe: number | null;
  pe: number | null;
  ret_3m: number | null;
  is_psu?: boolean;
  confidence?: "high" | "medium" | "low";
}
export interface DailyPicks {
  available: boolean;
  as_of: string;
  n_stocks: number;
  n_candidates?: number;
  n_eligible?: number;
  sectors?: string[];
  caps?: string[];
  per_sector?: number;
  narrow_universe?: boolean;
  price_as_of?: string | null;
  price_stale?: boolean;
  stocks: DailyPick[];
  methodology?: string;
  caveats: string[];
  narration?: { summary?: string; stock_rationales?: { ticker: string; why: string }[]; notes?: string[] };
  disclaimer?: string;
  cached?: boolean;
  generated_at?: string;
}
/** The adviser-only Daily 5 attention screen (same server gate as the equity basket).
 *  `sectors`/`caps` = the adviser's declared interest (empty = whole market).
 *  `narrate` is LAZY: the deterministic list is ₹0 and always free; pass true only when
 *  the adviser asks for the written rationale (the only part that costs money). */
export function getDailyPicks(opts?: {
  sectors?: string[]; caps?: string[]; narrate?: boolean;
}): Promise<DailyPicks> {
  const q = new URLSearchParams();
  if (opts?.sectors?.length) q.set("sectors", opts.sectors.join(","));
  if (opts?.caps?.length) q.set("caps", opts.caps.join(","));
  if (opts?.narrate) q.set("narrate", "1");
  const qs = q.toString();
  return request<DailyPicks>(`/daily-picks${qs ? `?${qs}` : ""}`);
}

// Model portfolios — save a basket, then drift-check it over time (SEBI review cadence).
export interface ModelPortfolioDrift {
  added: string[]; dropped: string[];
  weight_drift: { ticker: string; from: number; to: number; delta_pp: number }[];
  confidence_downgrades: string[]; n_changes: number; material: boolean; summary: string;
}
export interface ModelPortfolio {
  id: string; name: string; kind: string; amount: number; risk: string; horizon: string;
  created_at: string;
  stocks: { ticker: string; name?: string; weight?: number; cap?: string }[];
  drift?: ModelPortfolioDrift | null;
}
export function saveModelPortfolio(p: { name?: string; amount: number; risk: string; horizon?: string; stocks: unknown[]; caps?: { Large: number; Mid: number; Small: number } | null }): Promise<{ ok: boolean; id: string }> {
  return request<{ ok: boolean; id: string }>(`/equity/basket/save`, { method: "POST", json: p });
}
export function getModelPortfolios(): Promise<{ portfolios: ModelPortfolio[]; n: number }> {
  return request<{ portfolios: ModelPortfolio[]; n: number }>(`/model-portfolios`);
}
export function deleteModelPortfolio(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/model-portfolios/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface TrackRecordBucket {
  bucket: string;
  n: number;
  avg_return_pct: number;
  avg_excess_pct: number;
  hit_rate_pct: number;
}
export interface TrackRecord {
  status: "building" | "ready" | "unavailable";
  horizon_days: number;
  scored: number;
  maturing: number;
  buckets: TrackRecordBucket[];
}
/** Conviction track record — forward returns of matured verdicts vs NIFTY. */
export function getTrackRecord(): Promise<TrackRecord> {
  return request<TrackRecord>(`/reports/track-record`);
}

// ── Practice digest (advisor's whole book aggregated) ─────────────────────────
export interface ExposureRow {
  label: string;
  ticker: string;
  invested: number;
  clients: number;
  pct_of_book: number | null;
}
export interface FundVsIndexRow { name: string; benchmark: string; excess_3y: number | null; beat: boolean }
export interface PracticeDigest {
  n_clients: number;
  book_invested: number;
  valued: boolean;
  top_equity: ExposureRow[];
  top_funds: ExposureRow[];
  concentration: { top5_weight_pct: number | null; hhi: number | null };
  over_concentrated: { client: string; holding: string; pct: number }[];
  funds_vs_index?: { n: number; n_beat: number; funds: FundVsIndexRow[] };
}
export function getPracticeDigest(): Promise<PracticeDigest> {
  return request<PracticeDigest>(`/book/digest`);
}

export interface CrowdedStock { ticker: string; n_clients: number; value: number | null; pct_of_book: number | null }
export interface CorrPair { a: string; b: string; corr: number }
export interface BookCrowding { available: boolean; as_of?: string | null; n_clients?: number; crowded: CrowdedStock[]; pairs: CorrPair[] }
export function getBookCrowding(): Promise<BookCrowding> {
  return request<BookCrowding>(`/book/crowding`);
}

export interface StressExposed { ticker: string; sector: string; value: number; pct_of_book: number }
export interface StressClient { client: string; pressured_value: number; pct_of_equity: number | null }
export interface StressScenario {
  key: string; title: string; shock: string; why: string;
  pressured_value: number; pressured_pct: number;
  cushioned_value: number; cushioned_pct: number;
  mixed_pct: number; limited_pct: number;
  top_exposed: StressExposed[]; top_clients: StressClient[];
}
export interface BookMacroStress { available: boolean; as_of?: string | null; book_equity?: number; n_clients?: number; scenarios: StressScenario[] }
export function getBookMacroStress(): Promise<BookMacroStress> {
  return request<BookMacroStress>(`/book/macro-stress`);
}

export interface BriefMover { ticker: string; pct: number; value: number }
export interface BriefAttention { kind: string; severity: string; ticker: string | null; title: string; detail: string; n_clients: number | null }
export interface BriefItem { key: string; title: string }
export interface LiveStressExposed { ticker: string; sector: string; value: number; pct_of_book: number }
export interface LiveStress {
  title: string; shock: string; driver: string; driver_change_1m_pct: number | null;
  sensitive: string; pressured_pct: number; top_exposed: LiveStressExposed[];
}
export interface BookBrief {
  available: boolean; date: string; as_of?: string | null;
  market: { headline?: string; regime?: string; tailwinds?: string[]; headwinds?: string[] };
  book: { value?: number; day_change_pct?: number | null; as_of?: string | null };
  movers: { up: BriefMover[]; down: BriefMover[] };
  attention: BriefAttention[];
  whats_new: { new: BriefItem[]; resolved: BriefItem[] };
  live_stress: LiveStress | null;
  reporting_soon?: UpcomingResult[];
  focus: string;
}
export function getBookBrief(): Promise<BookBrief> {
  return request<BookBrief>(`/book/brief`);
}

export interface UpcomingResult { ticker: string; date: string; days_away: number; n_clients: number; clients: string[] }
export interface ReportedResult { ticker: string; date: string; surprise_pct: number | null; verdict: "beat" | "miss" | "inline" | null; n_clients: number; clients: string[] }
export interface BookResults { available: boolean; upcoming: UpcomingResult[]; reported: ReportedResult[]; n_upcoming?: number; n_reported?: number }
export function getBookResults(): Promise<BookResults> {
  return request<BookResults>(`/book/results`);
}

// Thesis Monitor — the adviser "research review queue" (IFA/MFD only).
export interface ThesisItem {
  watch_id: string;
  ticker: string;
  kind: "risk_level" | "upside_range" | "entry_range" | "corp_action" | "earnings_review" | "stale_review" | "levels_superseded" | "quote_gap";
  title: string;
  is_new?: boolean;
  fired_at?: string | null;
  report_date?: string | null;
  level?: number | null;
  price?: number | null;
  context?: string;
  stale?: boolean;
  holders?: { count: number; names: string[] };
  checklist?: string[];
  earnings_date?: string | null;
  days_away?: number | null;
}
export interface ThesisMonitor { available: boolean; items: ThesisItem[]; n_watches: number; as_of?: string | null }
export function getThesisMonitor(): Promise<ThesisMonitor> {
  return request<ThesisMonitor>(`/thesis/monitor`);
}
export function closeThesisWatch(watchId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/thesis/watch/${encodeURIComponent(watchId)}`, { method: "DELETE" });
}
export function watchThesis(ticker: string): Promise<{ ok: boolean; watch_id: string }> {
  return request<{ ok: boolean; watch_id: string }>(`/thesis/watch`, { method: "POST", json: { ticker } });
}
export function rebaseThesis(watchId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/thesis/rebase/${encodeURIComponent(watchId)}`, { method: "POST" });
}

// Compliance — AI-use disclosure + AI-use log (IFA/MFD only).
export interface AiUseRow {
  date: string; generated_at: string; ticker: string; research_stance: string;
  tier?: string | null; ai_system: string; model: string; model_basis: string; qc_status: string;
  generated_by: string; run_type: string; report_id?: string;
}
export interface AiUseLog {
  version: string; exported_at: string; n: number; truncated: boolean;
  note: string; rows: AiUseRow[]; disclosure: string;
}
export function getAiUseLog(limit = 200): Promise<AiUseLog> {
  return request<AiUseLog>(`/compliance/ai-use-log?limit=${limit}`);
}

// MITC (Most Important Terms & Conditions) generator — RIAs only.
export interface MitcProfile {
  entity_name?: string; sebi_reg_no?: string; basl_membership_no?: string;
  principal_officer?: string; address?: string; email?: string; phone?: string;
  fee_mode?: "fixed" | "aua" | ""; fee_amount?: number | null; aua_pct?: number | null;
  grievance_officer?: string; grievance_email?: string; grievance_phone?: string;
}
export interface MitcDoc {
  as_of: string;
  identity: { entity_name: string; sebi_reg_no: string; basl_membership_no: string;
    principal_officer: string; address: string; email: string; phone: string };
  fee: { mode: string; description: string; within_cap: boolean | null; warning: string | null };
  sections: { n: number; title: string; body: string; additional?: boolean }[];
  warnings: string[];
  caveats: string[];
}
export function buildMitc(profile: MitcProfile): Promise<MitcDoc> {
  return request<MitcDoc>(`/compliance/mitc`, { method: "POST", json: profile });
}

// ── Explain layer (LLM narration of analytics panels; advisor-gated) ────────
export interface ExplainResult {
  available: boolean; text?: string; mode?: string; language?: string;
  /** Retail only: the AI-credit wallet is empty. A distinct case from "we
   *  couldn't ground an explanation" — the fix is credits, not the panel. */
  budget_exhausted?: boolean;
}
export type ExplainLanguage = "en" | "hi" | "gu" | "ta" | "te" | "mr" | "kn" | "bn";
/** Plain-language narration of a panel's exact JSON. Grounded server-side:
 *  every number in the output is validated against the payload; cached per
 *  (surface, payload, mode, language) so repeat views are free. */
export function explainPanel(payload: {
  surface: string;
  payload: unknown;
  mode?: "advisor" | "client_note";
  language?: ExplainLanguage;
}): Promise<ExplainResult> {
  return request<ExplainResult>(`/explain`, { method: "POST", json: payload });
}

// ── Ask (grounded multi-turn Q&A over one panel; advisor-gated) ─────────────
export interface AskTurn { role: "user" | "assistant"; text: string }
export interface AskResult { available: boolean; text?: string; language?: string }
/** Ask a free-text question about a panel's exact JSON. Server grounds every
 *  number against the payload, refuses advice, and compliance-lints — same
 *  guarantee as explainPanel, but conversational. Send the full `history` each
 *  turn (the server is stateless). */
export function askPanel(body: {
  surface: string;
  payload: unknown;
  question: string;
  history?: AskTurn[];
  language?: ExplainLanguage;
}): Promise<AskResult> {
  return request<AskResult>(`/ask`, { method: "POST", json: body });
}

// ── Prospect proposal (advisor client-acquisition; IFA + MFD) ──────────────
export interface ProspectDiagnostic { severity: "note" | "attention"; kind: string; text: string }
export interface ProspectSnapshot {
  total_value: number; equity_value: number; mf_value: number;
  n_stocks: number; n_funds: number; as_of?: string | null;
}
export interface FundVsIndexEntry {
  fund_3y?: number | null; benchmark?: string; index_3y?: number | null;
  excess_3y?: number | null; beat?: boolean | null; category?: string;
  category_beat_rate?: number | null;
}
export interface ProspectAnalysis {
  snapshot: ProspectSnapshot;
  xray: XRayResponse;
  funds_vs_index: Record<string, FundVsIndexEntry>;
  diagnostics: ProspectDiagnostic[];
}
/** Stateless diagnostic of a PROSPECT's pasted portfolio — nothing is saved.
 *  Equity sleeve is IFA-only (403 for MFD, mirroring the client rules). */
export function analyzeProspect(payload: {
  holdings?: { ticker: string; qty: number; avg_buy_price?: number | null }[];
  mf_holdings?: { scheme_code: string; isin?: string | null; units: number; scheme_name?: string | null }[];
}): Promise<ProspectAnalysis> {
  return request<ProspectAnalysis>(`/prospect/analyze`, { method: "POST", json: payload });
}

export interface Factor { key: string; label: string; tilt: string; stance: "aggressive" | "defensive" | "neutral"; detail: string; value: string }
export interface BookFactors { available: boolean; as_of?: string | null; equity_value?: number; factors: Factor[]; summary?: string }
export function getBookFactors(): Promise<BookFactors> {
  return request<BookFactors>(`/book/factors`);
}

export function getReportByDocId(docId: string): Promise<ReportResponse> {
  return request<ReportResponse>(`/reports/view/${encodeURIComponent(docId)}`);
}

// ── /billing ─────────────────────────────────────────────────────────
//
// Credits-based pricing. Each fresh briefing = 1 credit; cached returns
// and in-flight dedup are free. Packs are one-shot purchases via
// Razorpay Checkout — order created server-side, credits applied on
// webhook (not on client confirmation, which we don't trust).

export interface Pack {
  sku: string;
  name: string;
  credits: number;
  price_paise: number;
  price_rupees: number;
  per_briefing_rupees: number;
  audience: "individual" | "ifa";
}

export function getPacks(
  audience?: "individual" | "ifa",
): Promise<{ packs: Pack[] }> {
  const qs = audience ? `?audience=${audience}` : "";
  return request<{ packs: Pack[] }>(`/billing/packs${qs}`);
}

export function getMyCredits(): Promise<{ credits: number; enforced: boolean }> {
  return request<{ credits: number; enforced: boolean }>("/me/credits");
}

export interface RazorpayOrder {
  order_id: string;
  amount: number;     // paise
  currency: "INR";
  key_id: string;     // Razorpay public key — safe to ship to client
  pack: { sku: string; name: string; credits: number };
}

/** Single-action AI credit buy (Razorpay): exactly the credits ONE feature
 *  costs, server-priced by feature. Lands in the AI wallet on the webhook. */
export interface AiSingle {
  feature: string;
  sku: string;
  credits: number;
  price_inr: number;
  label: string;        // "Deep Dive", "Health Report", "investigation" …
}

export function getAiSingles(): Promise<{ singles: AiSingle[] }> {
  return request<{ singles: AiSingle[] }>("/billing/ai-singles");
}

export interface AiSingleOrder {
  order_id: string;
  amount: number;       // paise
  currency: "INR";
  key_id: string;
  single: AiSingle;
}

export function createAiSingleOrder(feature: string): Promise<AiSingleOrder> {
  return request<AiSingleOrder>("/billing/ai-order", {
    method: "POST",
    json: { feature },
  });
}

export function createBillingOrder(packSku: string): Promise<RazorpayOrder> {
  return request<RazorpayOrder>("/billing/order", {
    method: "POST",
    json: { pack: packSku },
  });
}

export interface RazorpaySubscriptionCheckout {
  subscription_id: string;
  key_id: string;
  plan: "monthly" | "annual";
  amount: number;
  currency: "INR";
}

export function createRetailSubscription(
  plan: "monthly" | "annual",
): Promise<RazorpaySubscriptionCheckout> {
  return request<RazorpaySubscriptionCheckout>("/billing/subscription", {
    method: "POST", json: { plan },
  });
}

export function cancelRetailSubscription(): Promise<{ ok: boolean; ends_at?: string | null }> {
  return request<{ ok: boolean; ends_at?: string | null }>("/billing/subscription/cancel", {
    method: "POST",
  });
}

// Server returns this in the 402 response body when /chat/v2/start
// runs out of credits. The OutOfCreditsModal reads it to decide what
// to suggest (e.g. "you need 1 credit, balance 0 → buy Starter").
export interface InsufficientCreditsDetail {
  error: "INSUFFICIENT_CREDITS";
  balance: number;
  needed: number;
  ticker: string;
}

/** A 402 is not always "out of credits" — show the server's actual reason.
 *
 * 2026-09-07. A retail user bought 65 AI credits, pressed "Investigate this
 * move · 3 credits", and was told "You've used this month's AI credits. Top up".
 * He had 65. The server had said SUBSCRIPTION_REQUIRED: the expensive SKUs
 * (deep dive, Grok investigation, health report) sit behind a live subscription
 * regardless of wallet balance. Four separate components each hardcoded the
 * credits copy for every 402, so we told a paying customer that more money
 * would fix a problem more money cannot fix — and he paid again.
 *
 * `require_retail_access` and `_dual_meter_gate` both send a `message` written
 * for the specific gate that refused. Render that; keep `fallback` only for a
 * 402 that carries no detail. */
export function gateMessage(err: unknown, fallback: string): string {
  const detail = (err as { body?: { detail?: { error?: string; message?: string } } })
    ?.body?.detail;
  if (typeof detail?.message === "string" && detail.message.trim()) return detail.message;
  if (detail?.error === "SUBSCRIPTION_REQUIRED" || detail?.error === "SUBSCRIPTION_EXPIRED") {
    return "StockSense Pro is required for this. Your AI credits stay on your account.";
  }
  return fallback;
}

/** Which wall a 402 is, so the UI can offer the RIGHT door. `subscribe` /
 *  `renew` are the retail subscription gate (SUBSCRIPTION_REQUIRED / _EXPIRED);
 *  `credits` is a wallet balance problem. `canBuyCredits` is the server's own
 *  word that a purchased pack unlocks this SKU without a subscription — the
 *  Deep Dive case. Pure: the test mock passes it through (see test/setup.ts). */
export type GateKind = "subscribe" | "renew" | "credits" | null;
export function gateKind(err: unknown): { kind: GateKind; canBuyCredits: boolean } {
  const e = err as { status?: number; body?: { detail?: { error?: string; can_buy_credits?: boolean } } } | undefined;
  if (!e || typeof e !== "object" || e.status !== 402) return { kind: null, canBuyCredits: false };
  const d = e.body?.detail;
  const canBuyCredits = d?.can_buy_credits === true;
  if (d?.error === "SUBSCRIPTION_REQUIRED") return { kind: "subscribe", canBuyCredits };
  if (d?.error === "SUBSCRIPTION_EXPIRED") return { kind: "renew", canBuyCredits };
  return { kind: "credits", canBuyCredits: true };
}

export function isInsufficientCredits(
  err: unknown,
): err is ApiError & { body: { detail: InsufficientCreditsDetail } } {
  if (!(err instanceof ApiError) || err.status !== 402) return false;
  const body = err.body as { detail?: { error?: string } } | undefined;
  return body?.detail?.error === "INSUFFICIENT_CREDITS";
}

// ── /clients (IFA client book) ───────────────────────────────────────
//
// Top-level `clients/{id}` Firestore collection with `ifa_uid` filter
// (server-side). Holdings embedded as a JSON field. See
// tools/clients_schema.py for the canonical shape.

export type RiskProfile = "conservative" | "moderate" | "aggressive";

export interface ClientHolding {
  ticker: string;
  qty: number;
  avg_buy_price: number;
  // ISO YYYY-MM-DD — present when the agentic CSV parser detected a
  // broker tradebook date column. Absent when the source was a positions
  // snapshot (e.g. ICICI portfolio export) with no transaction history.
  buy_date?: string;
}

export interface TargetAllocation {
  equity_pct: number;
  debt_pct: number;
  gold_pct: number;
  cash_pct: number;
}

export interface Client {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  risk_profile: RiskProfile;
  onboarding_note?: string;
  age?: number;
  horizon_years?: number;
  goal?: string;
  target_allocation?: TargetAllocation;
  holdings?: ClientHolding[];
  mf_holdings?: MFHolding[];
  notes?: ClientNote[];
  advisory_notes?: AdvisoryNote[];
  created_at?: string;
  updated_at?: string;
}

export interface AdvisoryNote {
  id: string;
  ts: string;            // IST ISO timestamp
  draft: string;         // the original AI draft (audit)
  final: string;         // the adviser-reviewed/edited text (what they own)
  inputs_summary?: unknown;  // snapshot of the grounded inputs the AI saw (audit)
}

export interface ClientNote {
  id: string;
  ts: string;     // IST ISO timestamp
  text: string;
}

export interface CreateClientPayload {
  name: string;
  email?: string;
  phone?: string;
  risk_profile?: RiskProfile;
  onboarding_note?: string;
  age?: number;
  horizon_years?: number;
  goal?: string;
  target_allocation?: TargetAllocation;
  holdings?: ClientHolding[];
  mf_holdings?: MFHolding[];
}

export function getClients(): Promise<{ clients: Client[] }> {
  return request<{ clients: Client[] }>("/clients");
}

export function getClient(clientId: string): Promise<Client> {
  return request<Client>(`/clients/${encodeURIComponent(clientId)}`);
}

export function createClient(payload: CreateClientPayload): Promise<Client> {
  return request<Client>("/clients", {
    method: "POST",
    json: payload,
  });
}

export function addClientNote(
  clientId: string,
  text: string,
): Promise<{ notes: ClientNote[] }> {
  return request<{ notes: ClientNote[] }>(
    `/clients/${encodeURIComponent(clientId)}/notes`,
    { method: "POST", json: { text } },
  );
}

export function deleteClientNote(
  clientId: string,
  noteId: string,
): Promise<{ notes: ClientNote[] }> {
  return request<{ notes: ClientNote[] }>(
    `/clients/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(noteId)}`,
    { method: "DELETE" },
  );
}

export function updateClient(
  clientId: string,
  payload: Partial<CreateClientPayload>,
): Promise<Client> {
  return request<Client>(`/clients/${encodeURIComponent(clientId)}`, {
    method: "PUT",
    json: payload,
  });
}

// ── AI advisory note (advisor-only; grounded; adviser-owned) ──────────────
export interface AdvisoryDraft {
  draft: string;
  inputs_summary?: unknown;
  credits_remaining?: number | null;
}
export function generateAdvisoryNote(clientId: string): Promise<AdvisoryDraft> {
  return request<AdvisoryDraft>(
    `/clients/${encodeURIComponent(clientId)}/advisory-note/generate`,
    { method: "POST" },
  );
}
export function saveAdvisoryNote(
  clientId: string,
  payload: { draft: string; final: string; inputs_summary?: unknown },
): Promise<{ advisory_notes: AdvisoryNote[] }> {
  return request<{ advisory_notes: AdvisoryNote[] }>(
    `/clients/${encodeURIComponent(clientId)}/advisory-note`,
    { method: "POST", json: payload },
  );
}
export function deleteAdvisoryNote(
  clientId: string,
  noteId: string,
): Promise<{ advisory_notes: AdvisoryNote[] }> {
  return request<{ advisory_notes: AdvisoryNote[] }>(
    `/clients/${encodeURIComponent(clientId)}/advisory-note/${encodeURIComponent(noteId)}`,
    { method: "DELETE" },
  );
}

export function deleteClient(clientId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/clients/${encodeURIComponent(clientId)}`, {
    method: "DELETE",
  });
}

// ── Bulk review generation (draft the whole book in one action) ───────────
export interface BulkReviewDraft {
  client_id: string;
  ok: boolean;
  client_name?: string;
  draft?: string;
  inputs_summary?: unknown;
  error?: string;
}
export interface BulkReviewResult {
  available: boolean;
  reason?: string;
  drafts: BulkReviewDraft[];
  counts?: { requested: number; generated: number; failed: number; skipped: number };
  quota?: { plan: string; plan_label: string; used: number; limit: number; remaining: number };
  notes?: string[];
  disclaimer?: string;
}
/** Draft advisory notes for several clients in one call. Persists nothing —
 * each draft is reviewed/edited/saved individually via saveAdvisoryNote. */
export function bulkGenerateReviews(clientIds?: string[]): Promise<BulkReviewResult> {
  return request<BulkReviewResult>("/clients/review-packs/bulk", {
    method: "POST", json: { client_ids: clientIds ?? [] },
  });
}

export interface BulkCreateResult {
  created: number;
  updated: number;
  created_ids: string[];
  updated_ids: string[];
}

export function bulkCreateClients(
  clients: CreateClientPayload[],
): Promise<BulkCreateResult> {
  return request<BulkCreateResult>("/clients/bulk", {
    method: "POST",
    json: { clients },
  });
}

// ── Agentic broker-CSV parser for client holdings ─────────────────────
//
// Real broker statements (Zerodha, ICICI Direct, Groww, etc.) have ONLY
// holdings data — never the client's name/phone/email. The IFA uploads
// the broker statement separately from the client form. Backend resolves
// ticker/ISIN/name to canonical NSE tickers via fuzzy match + Claude
// Haiku tiebreaker. UI shows preview before commit.

export type ParseConfidence = "HIGH" | "MEDIUM" | "LOW";

export interface ParsedHolding {
  ticker: string | null;
  qty: number;
  cost: number;             // agentic parser uses `cost`; backend
                            // maps to avg_buy_price on save
  buy_date?: string;        // ISO YYYY-MM-DD; present when the source
                            // CSV had a buy_date / trade_date column
  company_name?: string;
  raw_name?: string;
  confidence: ParseConfidence;
  needs_review?: boolean;
  isin?: string;
  claude_resolved?: boolean;
}

export interface ParseClientCsvResult {
  holdings: ParsedHolding[];
  review_needed: ParsedHolding[];
  unresolved: ParsedHolding[];
  total_rows: number;
  resolved: number;
  format_detected: "ticker" | "isin" | "name";
}

export function parseClientCsv(
  csvContent: string,
): Promise<ParseClientCsvResult> {
  return request<ParseClientCsvResult>("/clients/parse-csv", {
    method: "POST",
    json: { csv_content: csvContent },
  });
}

/**
 * Same agentic parser as parseClientCsv, but hits the legacy
 * /portfolio/parse endpoint that the apex SPA also uses. Returns the
 * same shape. Use this for the individual user's OWN portfolio upload
 * (IndividualEmpty day-zero); use parseClientCsv when an IFA is
 * uploading a client's broker statement.
 */
export function parseMyPortfolioCsv(
  csvContent: string,
): Promise<ParseClientCsvResult> {
  return request<ParseClientCsvResult>("/portfolio/parse", {
    method: "POST",
    json: { csv_content: csvContent },
  });
}

// ── /trajectory (daily portfolio value series) ───────────────────────
//
// Backed by BQ historical closes joined against the user's CURRENT
// holdings. The sparkline therefore reflects real price moves over the
// past N days, not a synthetic walk. Honest constraint: holdings are
// treated as static-current (we don't yet have a transaction log), so
// the trace is "what if today's basket existed the whole window" —
// shape-correct, not historical-attribution-correct.

export interface TrajectoryPoint {
  date: string;   // YYYY-MM-DD (NSE trading day)
  value: number;  // ₹, sum across resolved holdings
}

export interface TrajectoryResponse {
  series: TrajectoryPoint[];
  as_of: string | null;
  days: number;
  resolved_tickers: number;
  unresolved_tickers: string[];
  /** "transactions" = real value path from the ledger (+ twr); "static_basket"
   *  = today's holdings back-projected flat (the synthetic fallback). */
  source?: "transactions" | "static_basket";
  twr?: number | null;
}

export function getPortfolioTrajectory(
  days = 30,
): Promise<TrajectoryResponse> {
  return request<TrajectoryResponse>(`/portfolio/trajectory?days=${days}`);
}

export function getClientTrajectory(
  clientId: string,
  days = 30,
): Promise<TrajectoryResponse> {
  return request<TrajectoryResponse>(
    `/clients/${encodeURIComponent(clientId)}/trajectory?days=${days}`,
  );
}

// ── Book-level exposure (IFA dashboard aggregation) ──────────────────
export interface BookHolder { id: string; name: string; qty: number; value: number | null }
export interface BookExposureResponse {
  n_clients: number;
  n_clients_with_holdings: number;
  book_value?: number;
  as_of?: string | null;
  stocks: {
    ticker: string; total_qty: number; cmp: number | null; value: number | null;
    n_clients: number; pct_of_book: number | null; clients: BookHolder[];
  }[];
  funds: {
    scheme_code: string; scheme_name: string; total_units: number; nav: number | null;
    value: number | null; n_clients: number; pct_of_book: number | null; clients: BookHolder[];
  }[];
  by_sector: { name: string; value: number; pct: number | null }[];
}

export interface BookDriftResponse {
  clients: {
    id: string; name: string; risk_profile: string; growth_pct: number;
    band: [number, number]; drift_pts: number; flagged: boolean; value: number;
  }[];
  n_flagged: number;
  as_of?: string | null;
  notes: string[];
}

export function getBookDrift(): Promise<BookDriftResponse> {
  return request("/book/drift");
}

export function getBookExposure(): Promise<BookExposureResponse> {
  return request("/book/exposure");
}

// ── Transactions ledger + performance/tax (Phase D) ──────────────────
export interface Txn {
  asset: "equity" | "mf";
  key: string;          // NSE ticker | AMFI scheme code
  name?: string;
  side: "BUY" | "SELL";
  date: string;         // YYYY-MM-DD
  qty: number;
  price: number;
  amount?: number;     // Stated consideration, when supplied by the statement
  kind?: string;        // CAS classification; idcw_reinvest is not investor money
  folio?: string;
  source?: string;
}

export interface TxnParseResult {
  transactions: Txn[];
  unresolved: { raw: string; reason: string }[];
  total_rows: number;
}

/** Your money-weighted return vs the SAME rupees on the SAME days in an index
 *  fund. See tools/pme.py — `available: false` carries a `reason` that is meant
 *  to be shown, not swallowed: it refuses on a reconstructed ledger rather than
 *  quoting a number it cannot support. */
export interface PortfolioIrr {
  available: boolean;
  reason?: string;
  benchmark_label?: string;
  your_xirr_pct?: number;
  benchmark_xirr_pct?: number;
  difference_pp?: number;
  ahead?: boolean;
  from_date?: string;
  to_date?: string;
  n_flows?: number;
  your_value?: number | null;
  benchmark_value?: number;
  notes?: string[];
  method?: string;
  reconstructed?: boolean;
}

export function getPortfolioIrr(benchmark?: string): Promise<PortfolioIrr> {
  const q = benchmark ? `?benchmark=${encodeURIComponent(benchmark)}` : "";
  return request<PortfolioIrr>(`/portfolio/irr${q}`);
}

export interface ClientPerformance {
  n_transactions: number;
  xirr_pct?: number | null;
  invested?: number;
  withdrawn?: number;
  open_cost_basis?: number;
  current_value?: number | null;
  realized_gain?: number;
  unrealized_gain?: number | null;
  warnings?: string[];
  reconstructed?: boolean;      // ledger synthesized from the holdings snapshot
  estimated_dates?: number;     // # of holdings with an assumed (not real) buy date
}

export interface CapitalGainsFY {
  rows: {
    asset: string; key: string; name: string; qty: number;
    buy_date: string; sell_date: string; days_held: number;
    buy_value: number; sell_value: number; gain: number;
    term: "ST" | "LT" | "slab";
  }[];
  st_gain: number;
  lt_gain: number;
  slab_gain: number;
  proceeds: number;
}

export interface CapitalGainsReport {
  fys: Record<string, CapitalGainsFY>;
  open_lots: {
    asset: string; key: string; name: string; qty: number;
    buy_date: string; buy_value: number; days_held: number;
    is_long_term: boolean; becomes_lt_on: string | null;
  }[];
  warnings: string[];
  notes: string[];
  reconstructed?: boolean;
  estimated_dates?: number;
}

export function getPortfolioTransactions(): Promise<{ transactions: Txn[]; count: number }> {
  return request("/portfolio/transactions");
}

export function putPortfolioTransactions(transactions: Txn[]): Promise<{ transactions: Txn[]; count: number }> {
  return request("/portfolio/transactions", { method: "PUT", json: { transactions } });
}

export function getPortfolioPerformance(): Promise<ClientPerformance> {
  return request("/portfolio/performance");
}

export function getPortfolioCapitalGains(): Promise<CapitalGainsReport> {
  return request("/portfolio/capital-gains");
}

export function getClientTransactions(clientId: string): Promise<{ transactions: Txn[]; count: number }> {
  return request(`/clients/${encodeURIComponent(clientId)}/transactions`);
}

export function putClientTransactions(clientId: string, transactions: Txn[]): Promise<{ transactions: Txn[]; count: number }> {
  return request(`/clients/${encodeURIComponent(clientId)}/transactions`, {
    method: "PUT", json: { transactions },
  });
}

export function parseTransactionsCsv(csvContent: string): Promise<TxnParseResult> {
  return request("/transactions/parse", { method: "POST", json: { csv_content: csvContent } });
}

export function parseTransactionsFile(fileB64: string): Promise<TxnParseResult> {
  return request("/transactions/parse", { method: "POST", json: { file_b64: fileB64 } });
}

export function getClientPerformance(clientId: string): Promise<ClientPerformance> {
  return request(`/clients/${encodeURIComponent(clientId)}/performance`);
}

export function getClientCapitalGains(clientId: string): Promise<CapitalGainsReport> {
  return request(`/clients/${encodeURIComponent(clientId)}/capital-gains`);
}

// ── Portfolio X-Ray (deterministic analytics, ₹0) ────────────────────
export interface XRayResponse {
  as_of?: string | null;
  equity_value?: number;
  mf_value?: number;
  unpriced_tickers?: string[];
  data_notes?: string[];
  allocation?: {
    by_sector: { name: string; pct: number }[];
    by_cap_tier: { name: string; pct: number }[];
  };
  concentration?: {
    top_holdings: { ticker: string; pct: number }[];
    top5_pct: number;
    hhi: number;
    effective_positions: number | null;
    flags: string[];
  };
  risk?: {
    window_days: number;
    observations: number;
    volatility_ann_pct: number | null;
    max_drawdown_pct: number | null;
    var_1d_95_pct: number | null;
    var_1d_99_pct: number | null;
    beta_vs_nifty50: number | null;
    window_return_pct: number | null;
    nifty50_return_pct: number | null;
    excess_return_pct: number | null;
  };
  quality_tilt?: {
    weighted_pe: { value: number; coverage_pct: number } | null;
    weighted_roe_pct: { value: number; coverage_pct: number } | null;
    weighted_debt_equity: { value: number; coverage_pct: number } | null;
  };
  momentum?: {
    portfolio_6m_pct: number | null;
    market_6m_pct: number | null;
    excess_pct?: number;
    tilt?: "leading" | "lagging" | "inline";
  };
  mf_correlation?: {
    pairs: { a: string; b: string; correlation: number; flag: boolean }[];
    note: string;
  };
  asset_split?: { equity_pct: number; mf_pct: number };
  current_asset_mix?: { equity_pct: number; debt_pct: number; gold_pct: number; other_pct: number };
  mf_health?: MFHealth;
}

// ── Asset-allocation engine (advisory + retail educator) ──────────────────
export interface AllocationSuggestion {
  inputs: { age: number; risk: string; horizon_years: number };
  equity_pct: number;
  debt_pct: number;
  gold_pct: number;
  cash_pct: number;
  equity_split: { large: number; mid: number; small: number };
  rationale: string;
}
export function suggestAllocation(
  age: number,
  risk: string,
  horizon: number,
): Promise<AllocationSuggestion> {
  return request<AllocationSuggestion>(
    `/allocation/suggest?age=${age}&risk=${encodeURIComponent(risk)}&horizon=${horizon}`,
  );
}

export interface MFHealth {
  total_value: number;
  fund_count: number;
  category_allocation: { name: string; pct: number }[];
  category_hhi: number;
  top_category: { name: string; pct: number } | null;
  amc_allocation: { name: string; pct: number }[];
  amc_count: number;
  amc_hhi: number;
  top_amc: { name: string; pct: number } | null;
  asset_split: { equity_pct: number; debt_pct: number; hybrid_pct: number; other_pct: number };
  risk: { volatility_ann_pct: number | null; max_drawdown_pct: number | null; coverage_pct: number };
  quality: { weighted_cagr_3y_pct: number | null; below_category_median_pct: number | null; coverage_pct: number };
  flags: string[];
  data_notes: string[];
}

export function getPortfolioXray(days = 365): Promise<XRayResponse> {
  return request<XRayResponse>(`/portfolio/xray?days=${days}`);
}

export function getClientXray(clientId: string, days = 365): Promise<XRayResponse> {
  return request<XRayResponse>(
    `/clients/${encodeURIComponent(clientId)}/xray?days=${days}`,
  );
}

// Client review pack (ADVISER prep — X-ray + adviser-only thesis flags).
export interface ThesisFlag { ticker: string; kind: string; title: string; is_new: boolean }
export interface ReviewPack {
  client_name: string; as_of: string; n_holdings: number; n_funds: number;
  xray: XRayResponse; thesis_flags: ThesisFlag[]; adviser_only_note: string; disclaimer: string;
}
export function getClientReviewPack(clientId: string, days = 365): Promise<ReviewPack> {
  return request<ReviewPack>(`/clients/${encodeURIComponent(clientId)}/review-pack?days=${days}`);
}

// ── What-if simulation ───────────────────────────────────────────────────────

export interface SimulateChange {
  action: "add" | "remove" | "resize";
  kind?: "equity" | "mf";
  ticker?: string;
  scheme_code?: string;
  qty?: number;
  units?: number;
  amount_inr?: number;
}

export interface SimulateRequest {
  changes: SimulateChange[];
}

export interface SimDelta { before: number | null; after: number | null; delta: number | null }
export interface ShiftRow { name: string; before_pct: number | null; after_pct: number | null }
export interface ResultingWeight { ticker: string; before_pct: number | null; after_pct: number | null }

export interface SimTaxHolding {
  asset?: string; ticker: string; sold_qty: number;
  st_gain: number; lt_gain: number; slab_gain?: number;
  matched_qty: number; unmatched_qty: number;
}
export interface SimTax {
  available: boolean;
  reason?: string;
  realized_st_gain?: number;
  realized_lt_gain?: number;
  realized_slab_gain?: number;
  by_holding?: SimTaxHolding[];
  warnings?: string[];
  notes?: string[];
}

export interface SimulationResponse {
  delta: Record<string, SimDelta>;
  allocation_shift: { by_sector: ShiftRow[]; by_cap_tier: ShiftRow[] };
  quality: {
    weighted_pe: { before: number | null; after: number | null };
    weighted_roe_pct: { before: number | null; after: number | null };
  };
  resulting_weights: ResultingWeight[];
  tax: SimTax;
  mf_health?: { before: MFHealth | null; after: MFHealth | null };
  warnings: string[];
  notes: string[];
}

export function simulatePortfolio(body: SimulateRequest): Promise<SimulationResponse> {
  return request<SimulationResponse>("/portfolio/simulate", { method: "POST", json: body });
}

export function simulateClient(clientId: string, body: SimulateRequest): Promise<SimulationResponse> {
  return request<SimulationResponse>(
    `/clients/${encodeURIComponent(clientId)}/simulate`,
    { method: "POST", json: body },
  );
}

// ── /quote (bulk CMP lookup) ─────────────────────────────────────────
//
// Powers live AUM / lifetime P&L on the IFA dashboard (sum across all
// client holdings) and live values on ClientDetail. Backend caps at 200
// tickers per request — callers chunk if needed.

export interface Quote {
  ticker: string;
  cmp: number | null;
  change_pct: number | null;
  week52_high: number | null;
  week52_low: number | null;
  trade_date: string | null;
  data_source: string;
}

export function getQuotes(
  tickers: string[],
): Promise<{ as_of: string | null; quotes: Quote[] }> {
  return request<{ as_of: string | null; quotes: Quote[] }>("/quote", {
    method: "POST",
    json: { tickers },
  });
}

// ── Watchlist (stocks + funds the user tracks; not necessarily owned) ──────
export interface WatchItem {
  type: "stock" | "fund";
  id: string;                 // ticker (stock) | amfi_code (fund)
  name?: string | null;
  watch_price?: number | null;
  ref_price?: number | null;   // live price when the watch was set (fixes crossing direction)
  added_at?: string | null;
}
export function getWatchlist(): Promise<{ items: WatchItem[] }> {
  return request<{ items: WatchItem[] }>("/watchlist");
}
export function saveWatchlist(items: WatchItem[]): Promise<{ items: WatchItem[] }> {
  return request<{ items: WatchItem[] }>("/watchlist", { method: "POST", json: { items } });
}
export interface FundSnapshot {
  amfi_code: string;
  scheme_name: string | null;
  amc_name: string | null;
  category: string | null;
  nav: number | null;
  nav_date: string | null;
  cagr_1y: number | null;
  cagr_3y: number | null;
}
export function getMfSnapshot(codes: string[]): Promise<{ funds: FundSnapshot[] }> {
  return request<{ funds: FundSnapshot[] }>("/mf/snapshot", { method: "POST", json: { codes } });
}

// ── Data freshness (the "as of" date surfaced in the nav) ─────────────────────
export interface DataAsOf { equity_as_of: string | null; mf_as_of: string | null }
export function getDataAsOf(): Promise<DataAsOf> {
  return request<DataAsOf>("/market/data-asof");
}

// ── Market pulse (whole-market breadth + sector rotation) ─────────────────────
export interface MarketBreadth {
  available: boolean; as_of?: string | null; n?: number;
  pct_above_50dma?: number | null; pct_above_200dma?: number | null;
  new_highs?: number; new_lows?: number; advancers?: number; decliners?: number;
  read?: string;
}
export interface SectorRow { sector: string; n: number; ret_1m: number | null; ret_3m: number | null; trend: string }
export interface SectorRotation { available: boolean; sectors: SectorRow[]; leaders?: string[]; laggards?: string[] }
export interface MacroBackdrop {
  regime?: string; headline?: string; tailwinds?: string[]; headwinds?: string[];
  vix_percentile?: number;
  drivers?: { key: string; label: string; change_1m_pct: number | null; direction: string; india_impact: string }[];
}
export interface WorldMarket { name: string; symbol: string; ret_1w: number | null; ret_1m: number | null }
export interface GlobalTape {
  available: boolean; markets: WorldMarket[];
  vix?: { level: number | null; change_1m_pct: number | null };
  india_us_corr?: number | null; coupling?: string | null;
}
export interface MarketPulse { breadth: MarketBreadth; rotation: SectorRotation; backdrop?: MacroBackdrop; global?: GlobalTape }
export function getMarketPulse(): Promise<MarketPulse> {
  return request<MarketPulse>("/market/pulse");
}

export interface BreadthStock { ticker: string; close: number | null; pct: number | null; name: string | null; sector: string | null; ret_1m?: number | null }
export interface BreadthConstituents { available: boolean; filter?: string; label?: string; n?: number; stocks: BreadthStock[] }
export function getBreadthConstituents(filter: string): Promise<BreadthConstituents> {
  return request<BreadthConstituents>(`/market/breadth/${encodeURIComponent(filter)}`);
}
export function getSectorConstituents(sector: string): Promise<BreadthConstituents> {
  return request<BreadthConstituents>(`/market/sector/${encodeURIComponent(sector)}`);
}

// ── Active-vs-passive lens (MFD): beat-rates per category + index trackers ─────
export interface ApCategory {
  category: string; cat_id?: string; benchmark: string; n: number;
  beat_1y: number | null; beat_3y: number | null; beat_5y: number | null;
  index_1y: number | null; index_3y: number | null; index_5y: number | null;
  median_1y: number | null; median_3y: number | null; median_5y: number | null;
}
export interface ApScorecard { available: boolean; categories: ApCategory[]; caveat?: string }
// Drill-down: the funds behind a category's beat-rate, ranked by 3Y excess.
export interface CategoryFund { amfi_code: string; name: string; cagr_3y: number | null; excess_3y: number | null; beat: boolean }
export interface PassiveCategoryFunds { available: boolean; category?: string; benchmark?: string; index_3y?: number | null; n?: number; n_beat?: number; funds: CategoryFund[] }
export function getPassiveCategoryFunds(cat: string): Promise<PassiveCategoryFunds> {
  return request<PassiveCategoryFunds>(`/mf/passive-lens/category?cat=${encodeURIComponent(cat)}`);
}
export interface TrackerFund { amfi_code?: string | null; name: string; amc: string; vehicle?: string; cagr_3y: number | null; tracking_diff_3y: number | null }
export interface TrackerGroup { index: string; symbol: string; index_cagr_3y: number | null; n: number; n_etf?: number; n_fund?: number; funds: TrackerFund[] }
export interface TrackerView { available: boolean; groups: TrackerGroup[]; caveat?: string }
export interface PassiveLens { scorecard: ApScorecard; trackers: TrackerView }
export function getPassiveLens(): Promise<PassiveLens> {
  return request<PassiveLens>("/mf/passive-lens");
}

// ── Admin: AI usage tracking (owner-only; server-gated to ADMIN_EMAILS) ──────
export interface AiUsageBucket {
  model?: string; feature?: string; provider?: string; day?: string;
  calls: number; cost_usd: number; cost_inr: number; credits: number;
}
export interface AiUsageSummary {
  available: boolean;
  days: number;
  generated_at?: string;
  totals?: { calls: number; cost_usd: number; cost_inr: number; credits: number };
  by_model?: AiUsageBucket[];
  by_feature?: AiUsageBucket[];
  by_provider?: AiUsageBucket[];
  by_day?: AiUsageBucket[];
  /** GCP infra (Cloud Run / BigQuery / Firestore). `available: false` until the
   *  BigQuery billing export is enabled — the screen then shows the setup note. */
  infra?: {
    available: boolean; setup?: string; error?: string; currency?: string;
    gross?: number; credits?: number; net?: number; net_inr?: number;
    services?: { service: string; cost: number; credits: number; net: number; net_inr: number }[];
    note?: string;
  };
  all_in?: { ai_inr: number; infra_inr: number | null; total_inr: number | null };
}
export function getAdminAiUsage(days = 30): Promise<AiUsageSummary> {
  return request<AiUsageSummary>(`/admin/ai-usage?days=${days}`);
}

/** ACQUISITION FUNNEL — signup → activation → checkout → payment.
 *
 *  Reconstructed server-side from records that already existed (pro_users,
 *  the app-data user doc, payu_txns, ai_usage), plus the pre-auth half from
 *  the event ledger. `holdings` is presence only — nothing is decrypted. */
export interface AdminFunnelUser {
  uid: string;
  email: string;
  role: string;
  status: string;
  subscribed: boolean;
  has_paid: boolean;
  created_at: string | null;
  activated: boolean;
  has_holdings: boolean;
  ai_calls: number;
  checkout_attempts: number;
  txns: { txnid: string; status: string; payu_status?: string | null;
          amount?: string | null; plan?: string | null; at?: string | null }[];
}
export interface AdminFunnel {
  window_days: number;
  funnel_retail: {
    signed_up: number; opened_app: number; added_holdings: number;
    used_ai: number; reached_checkout: number; paid: number;
  };
  funnel_retail_pct: Record<string, number>;
  signups_by_day: Record<string, Record<string, number>>;
  by_role: Record<string, number>;
  txn_status: Record<string, number>;
  /** Razorpay briefing-credit pack orders, by status (second money rail). */
  razorpay_order_status?: Record<string, number>;
  /** `subscribed:true` with no successful payment — a manual grant, NOT revenue. */
  subscribed_without_payment: number;
  /** Transactions stuck pending/processing: money may be captured with no grant. */
  needs_attention: { uid: string; txnid: string; status: string; why: string }[];
  /** Pre-auth events. Empty until the ledger has been collecting. */
  events?: {
    available: boolean; error?: string;
    totals?: Record<string, number>;
    by_day?: Record<string, Record<string, number>>;
    by_source?: Record<string, number>;
    unique_anon?: number;
  };
  users: AdminFunnelUser[];
}
export function getAdminFunnel(days = 90): Promise<AdminFunnel> {
  return request<AdminFunnel>(`/admin/funnel?days=${days}`);
}

/** Retail FUND note — Haiku plain-English read over the deterministic fund
 *  scorecard (descriptive only; NOT the adviser MF research note). 3 credits. */
export interface FundNoteResult {
  available: boolean;
  text?: string;
  amfi_code?: string;
  scheme_name?: string | null;
  narrative?: string;
  nav_move?: { nav?: number; as_of?: string; change_1w_pct?: number | null; change_1m_pct?: number | null; change_1y_pct?: number | null } | null;
  plan_cost?: { plan?: string; already_direct?: boolean; drag_pct_per_year?: number | null } | null;
  disclaimer?: string;
  budget?: AiBudget;
}
export function fundNote(amfiCode: string, schemeName?: string | null): Promise<FundNoteResult> {
  return request<FundNoteResult>("/assistant/fund-note", {
    method: "POST",
    json: { amfi_code: amfiCode, scheme_name: schemeName },
  });
}

// ── Adviser (MFD/IFA) subscription — PayU ────────────────────────────────────
export interface AdviserPlan {
  sku: string; amount_inr: number; days: number; role: "mfd" | "ifa";
  name: string; briefing_credits: number; limits?: Record<string, number>;
}
export interface AdviserPlansResponse {
  configured: boolean;
  current: {
    plan: string; plan_label: string; subscribed: boolean; expires_at?: string | null;
    limits?: Record<string, number>;
    usage?: Record<string, { used: number; limit: number; remaining: number }>;
    briefing_credits?: number;
  };
  plans: AdviserPlan[];
}
export function getAdviserPlans(): Promise<AdviserPlansResponse> {
  return request<AdviserPlansResponse>("/payu/adviser-plans");
}
/** Start an adviser plan purchase: initiate then form-POST to PayU. */
export async function startAdviserPlan(planSku: string): Promise<void> {
  const r = await request<PayuInitiateResponse>("/payu/initiate", {
    method: "POST",
    json: { product: "adviser", plan: planSku },
  });
  payuSubmit(r.action_url, r.params);
}

/** "What does this alert mean for me" — Haiku over the alert's own pack (1 credit). */
export interface AlertExplainResult {
  available: boolean; text?: string; narrative?: string; budget?: AiBudget;
}
export function explainAlert(alert: Record<string, unknown>): Promise<AlertExplainResult> {
  return request<AlertExplainResult>("/assistant/alert-explain", { method: "POST", json: { alert } });
}

/** Stress-scenario narrator — what a market fall would have meant (3 credits). */
export interface ScenarioResult {
  available: boolean; text?: string; narrative?: string;
  stress_test?: { nifty_fall_pct: number; your_estimated_fall_inr: number }[];
  budget?: AiBudget;
}
export function runScenario(): Promise<ScenarioResult> {
  return request<ScenarioResult>("/assistant/scenario", { method: "POST", json: {} });
}

// ── Adviser AI drafts (metered by the adviser plan allowance, not credits) ────
export interface AdviserNoteResult {
  available: boolean; text?: string; narrative?: string; client_name?: string;
  disclaimer?: string;
  quota?: { plan: string; used: number; limit: number; remaining: number };
}
/** Drafted commentary for a client's periodic review (adviser edits + owns it). */
export function reviewNote(clientId: string): Promise<AdviserNoteResult> {
  return request<AdviserNoteResult>("/assistant/review-note", {
    method: "POST", json: { client_id: clientId },
  });
}
/** One-page observation note for a prospect's pasted portfolio. */
export function prospectNote(xray: unknown, name?: string): Promise<AdviserNoteResult> {
  return request<AdviserNoteResult>("/assistant/prospect-note", {
    method: "POST", json: { xray, name },
  });
}

/** Admin — what CLIENTS paid us and consumed (owner-only). */
export interface AdminClientRow {
  uid: string; email?: string | null; role?: string | null;
  calls: number; credits: number; cost_usd: number; cost_inr: number;
  top_features?: [string, number][];
}
export interface AdminClientsResponse {
  days: number;
  revenue: { available: boolean; total_inr?: number; n_payments?: number;
             by_product?: { product: string; count: number; amount_inr: number }[] };
  clients: AdminClientRow[];
  totals: { n_clients: number; credits_charged: number; calls: number; cogs_inr: number; gross_inr: number };
}
/** Admin — SES readiness (owner-only). Returns no credentials. */
export interface SesStatus {
  available: boolean;
  region?: string;
  sandbox?: boolean;
  production_access?: boolean;
  sending_enabled?: boolean;
  max_24h?: number;
  max_per_second?: number;
  sent_24h?: number;
  identities?: { identity: string; type?: string; verified: boolean }[];
  verified_identities?: string[];
  error?: string;
  note?: string;
  denied?: string[];
  config?: {
    email_provider?: string; digests_enabled?: boolean; effective_provider?: string;
    allowed_recipients?: string[]; config_set?: string | null;
  };
}
/** Admin — send ONE real email through the live provider, to prove the path.
 *  The recipient must still clear the server-side allow-list. */
export function sendAdminTestEmail(to: string, template: "receipt" | "expiring" | "ended" = "receipt"):
  Promise<{ provider: string; to: string; ok: boolean; message_id?: string; error?: string }> {
  return request("/admin/email-test", { method: "POST", json: { to, template } });
}

export function getAdminEmailStatus(): Promise<SesStatus> {
  return request<SesStatus>("/admin/email-status");
}

export function getAdminClients(days = 30): Promise<AdminClientsResponse> {
  return request<AdminClientsResponse>(`/admin/clients?days=${days}`);
}

/* ── Statement inbox ───────────────────────────────────────────────────────
 * A private forwarding address. The user sets one mail filter and their
 * statements keep the book current — no broker connection, no licence.
 */
export type InboxMessage = {
  id: string;
  at: string;
  from?: string;
  subject?: string;
  dkim_domain?: string | null;
  dkim_ok?: boolean;
  trusted?: boolean;
  attachments?: string[];
  status: "applied" | "quarantined" | "needs_password" | "ignored" | "failed"
        | "pending" | "confirmation" | "no_holdings" | "transactions" | "needs_review";
  /** Gmail forwarding-confirmation code, surfaced so auto-forwarding can be
   *  enabled without leaving the app. Only ever set for DKIM-verified
   *  google.com mail; never applied to holdings. */
  confirmation_code?: string | null;
  confirmation_url?: string | null;
  reason?: string;
  parsed?: {
    kind?: string; holdings?: number; funds?: number; unresolved?: number;
    transactions?: number;
    /** Positions whose cost basis came from the book we already held. */
    carried?: number;
    /** Positions new to the book, priced at the statement's market price — a
     *  CAS never reports what you paid. */
    seeded?: number;
    uncosted?: number;
  };
  /** How much of the statement's own stated total we could account for. Shown
   *  whether it was imported or held back — it is the number that says how much
   *  of the book we actually read. */
  coverage?: { parsed_value?: number | null; stated_total?: number | null;
               coverage_pct?: number | null };
  /** Set on an auto-applied import: the sync-log id that `undoBrokerSync` takes. */
  log_id?: string | null;
  /** True when the drop guard held this import back and it can be applied. */
  can_import?: boolean;
  /** Share of the existing book (by cost) this statement would have removed. */
  dropped_share?: number | null;
};

export function getInboxAddress(): Promise<{
  address: string;
  password_saved: boolean;
  trusted_senders: string[];
  my_addresses: string[];
  max_addresses: number;
}> {
  return request("/inbox/address");
}

/** Replace the extra addresses you forward from. The account email is always
 *  included and cannot be removed. */
export function setInboxSenders(addresses: string[]): Promise<{ my_addresses: string[] }> {
  return request("/inbox/senders", { method: "POST", json: { addresses } });
}

/** Burn the current address and issue a new one — for when one leaks. */
export function rotateInboxAddress(): Promise<{ address: string }> {
  return request("/inbox/rotate", { method: "POST", json: {} });
}

export function getInboxMessages(limit = 25): Promise<{ messages: InboxMessage[] }> {
  return request(`/inbox/messages?limit=${limit}`);
}

/** Remember (or, with an empty string, forget) the statement password. */
export function setInboxPassword(password: string): Promise<{ password_saved: boolean }> {
  return request("/inbox/password", { method: "POST", json: { password } });
}

/** Finish an import the drop guard held back. Statements apply automatically;
 *  this is only for one that would have removed a large part of the book. */
export function applyInboxMessage(messageId: string): Promise<{
  status: string; equity?: number; funds?: number; seeded?: number;
  uncosted?: number; log_id?: string;
}> {
  return request("/inbox/apply", { method: "POST", json: { message_id: messageId } });
}

/** Put the book back exactly as it was before a statement import. */
export function undoStatementImport(logId: string): Promise<{
  status: string; holdings?: number; funds?: number;
}> {
  return request("/broker/undo", { method: "POST", json: { log_id: logId } });
}

/** Join the waitlist for a tier that isn't open yet.
 *
 *  Fire-and-forget by design — but unlike its predecessor it actually leaves
 *  the browser. The old form wrote the address to the visitor's own
 *  localStorage and told them they were on a list that did not exist. */
export async function joinWaitlist(tier: "mfd" | "ifa", email: string): Promise<void> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "");
  if (!base) throw new Error("Not configured");
  const res = await fetch(`${base}/public/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tier, email }),
  });
  if (!res.ok) throw new Error("Could not add you to the list.");
}

// ── Adviser approvals (admin) ──────────────────────────────────────────────
// 2026-09-21: advisers start `pending`; nothing told the owner and there was no
// approve button. Stages: submitted = approve these; role_picked = never entered
// a registration number (nothing to approve — contact them); active.
export interface AdminAdviser {
  uid: string;
  email: string;
  name: string;
  role: "ifa" | "mfd" | "ra";
  status: string;
  stage: "submitted" | "role_picked" | "active";
  registration: string | null;
  accepted_active_sebi: boolean;
  joined: string | null;
  days_waiting: number | null;
  approved_at: string | null;
}
export function getAdminAdvisers(): Promise<{ advisers: AdminAdviser[]; counts: Record<string, number> }> {
  return request(`/admin/advisers`);
}
export function approveAdviser(uid: string): Promise<{ ok: boolean; already_active?: boolean }> {
  return request(`/admin/advisers/${encodeURIComponent(uid)}/approve`, { method: "POST" });
}
/** Called by the registration form after it saves: emails the owner that an
 *  adviser is waiting. Server-side it only ever reports on the caller, once. */
export function notifyAdviserRegistration(): Promise<{ ok: boolean; notified: boolean }> {
  return request(`/advisers/registration-submitted`, { method: "POST" });
}

/** Result of switching the caller's own persona from the sign-up role cards. */
export interface RoleSwitchResult {
  ok: boolean;
  role: "individual" | "ifa" | "mfd" | "ra";
  status: "pending" | "active";
  changed: boolean;
}
/** Change the signed-in user's role (backend-owned: it also sets `status`, which
 *  Firestore rules lock against the browser). 409 ROLE_LOCKED for an approved
 *  adviser — support moves those. */
export function switchAccountRole(role: string): Promise<RoleSwitchResult> {
  return request(`/account/role`, { json: { role } });
}

// ── Investment Adviser compliance (2026-09) ────────────────────────────────
// Backend: routers/ia_compliance.py. Every write appends to the adviser's
// hash-chained, encrypted record log; nothing is edited in place.
export type RiskCategory = "conservative" | "moderate" | "aggressive";

export interface IaQuestion { id: string; dimension: "capacity" | "tolerance"; text: string; options: string[] }
export interface IaQuestionnaire { version: string; questions: IaQuestion[] }

export interface IaProfileResult {
  questionnaire_version: string;
  capacity_score: number; tolerance_score: number;
  capacity: RiskCategory; tolerance: RiskCategory; category: RiskCategory;
  set_by: "capacity" | "tolerance"; flags: string[];
}
export interface IaSuitability {
  outcome: "suitable" | "outside_band" | "profile_stale" | "no_profile" | "invalid";
  needs_reason?: boolean; reasons?: string[]; detail?: string;
  category?: RiskCategory; band?: [number, number]; target_growth_pct?: number;
}
export interface IaRecord { seq: number; kind: string; created_at: string; hash: string; payload: Record<string, unknown> }
export interface IaClientState {
  profile: (IaProfileResult & { seq: number; created_at: string; stale: boolean; acknowledged: boolean }) | null;
  details: { family?: string | null; kyc_status?: string | null; kyc_ref?: string | null; receives_distribution?: boolean };
  agreement: { fee_mode: "fixed" | "aua"; fee_amount?: number; aua_pct?: number; aua_value?: number; signed_on: string; seq: number } | null;
  advice_count: number; last_advice_at: string | null;
  invoices: { invoice_no: string; amount: number; invoice_date: string; issues: string[] }[];
  acks: Record<string, { at: string; request_seq: number }>;
  open_ack_request: { seq: number; created_at: string; items: string[] } | null;
}
export interface IaClientView {
  client: { id: string; name: string; legacy_risk_profile?: string };
  state: IaClientState; records: IaRecord[];
}
export interface IaFamilyFees {
  family: string; members: string[]; fy: string; modes: string[]; billed: number;
  cap?: number; agreed?: number; issues: string[]; within_cap: boolean;
}
export interface IaOverviewRow {
  client_id: string; name: string; family: string | null;
  profile: { category: RiskCategory; created_at: string; stale: boolean; acknowledged: boolean } | null;
  advice_count: number; last_advice_at: string | null;
  agreement: { fee_mode: string; signed_on: string } | null;
  kyc_status: string | null; receives_distribution: boolean; open_ack_request: boolean; gaps: string[];
}
export interface IaChain { ok: boolean; n: number; head?: string; broken_at?: number; reason?: string }
export interface IaOverview {
  fy: string; clients: IaOverviewRow[]; families: IaFamilyFees[];
  separation_conflicts: string[]; open_grievances: number; chain: IaChain;
}
export interface IaGrievance {
  grievance_id: string; received_on: string; channel?: string | null; summary: string;
  scores_ref?: string | null; status: "open" | "resolved"; resolution?: string; resolved_on?: string;
  client_id?: string | null;
}

const IA = "/compliance/ia";
const iaClientPath = (clientId: string, tail = "") => `${IA}/clients/${encodeURIComponent(clientId)}${tail}`;
export const getIaQuestionnaire = () => request<IaQuestionnaire>(`${IA}/questionnaire`);
export const getIaClient = (clientId: string) => request<IaClientView>(iaClientPath(clientId));
export const saveIaRiskProfile = (clientId: string, answers: Record<string, string>, notes = "") =>
  request<{ result: IaProfileResult }>(iaClientPath(clientId, "/risk-profile"), { method: "POST", json: { answers, notes } });
export const saveIaAdvice = (clientId: string, body: {
  summary: string; rationale: string; target_growth_pct: number; override_reason?: string;
  advice_date?: string; actions?: { instrument: string; action: string; amount?: number }[];
}) => request<{ suitability: IaSuitability }>(iaClientPath(clientId, "/advice"), { method: "POST", json: body });
export const saveIaDetails = (clientId: string, body: {
  family?: string; kyc_status?: string; kyc_ref?: string; receives_distribution?: boolean;
}) => request<{ record: { seq: number } }>(iaClientPath(clientId, "/details"), { method: "POST", json: body });
export const saveIaAgreement = (clientId: string, body: {
  fee_mode: "fixed" | "aua"; fee_amount?: number; aua_pct?: number; aua_value?: number; signed_on: string;
}) => request<{ family_fees: IaFamilyFees }>(iaClientPath(clientId, "/agreement"), { method: "POST", json: body });
export const saveIaInvoice = (clientId: string, body: {
  invoice_no: string; amount: number; invoice_date: string; period_from: string; period_to: string;
}) => request<{ issues: string[]; family_fees: IaFamilyFees }>(iaClientPath(clientId, "/invoice"), { method: "POST", json: body });
export const createIaAckRequest = (clientId: string, items: string[]) =>
  request<{ url: string; expires_in_days: number }>(iaClientPath(clientId, "/ack-request"), { method: "POST", json: { items } });
const fyQuery = (fy: string) => (fy ? `?fy=${encodeURIComponent(fy)}` : "");
export const getIaOverview = (fy = "") => request<IaOverview>(`${IA}/overview${fyQuery(fy)}`);
export const getIaGrievances = () => request<{ grievances: IaGrievance[] }>(`${IA}/grievances`);
export const addIaGrievance = (body: { summary: string; received_on: string; channel?: string; scores_ref?: string; client_id?: string }) =>
  request<{ grievance_id: string }>(`${IA}/grievances`, { method: "POST", json: body });
export const resolveIaGrievance = (id: string, body: { resolution: string; resolved_on: string }) =>
  request<{ record: { seq: number } }>(`${IA}/grievances/${encodeURIComponent(id)}/resolve`, { method: "POST", json: body });
export const getIaAuditPack = (fy = "") => request<Record<string, unknown>>(`${IA}/audit-pack${fyQuery(fy)}`);
export const snapshotIaAuditPack = (fy = "") =>
  request<{ object: string; sha256: string }>(`${IA}/audit-pack/snapshot${fyQuery(fy)}`, { method: "POST" });

/** The CLIENT's acknowledgement page has no account, so it cannot use
 *  `request` (which requires a signed-in user). App Check still applies. */
async function publicRequest<T>(path: string, init: { method?: string; json?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...(await appCheckHeader()) };
  let body: string | undefined;
  if (init.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.json);
  }
  const method = init.method ?? (body !== undefined ? "POST" : "GET");
  const res = await fetch(`${BASE_URL}${path}`, { method, headers, body });
  if (!res.ok) {
    let payload: unknown;
    try { payload = await res.json(); } catch { payload = undefined; }
    throw new ApiError(serverMessage(payload) ?? `${method} ${path} → ${res.status}`, res.status, payload);
  }
  return (await res.json()) as T;
}
export interface IaAckView {
  adviser: { name?: string; registration?: string };
  client_first_name: string; items: string[]; already_acknowledged: boolean; requested_at: string;
  risk_profile?: { category: RiskCategory; capacity: RiskCategory; tolerance: RiskCategory; flags: string[]; recorded_on: string; band: number[] };
  agreement?: { fee_mode: "fixed" | "aua"; fee_amount?: number; aua_pct?: number; signed_on: string };
  ai_disclosure?: { version: string; text: string };
}
export const getIaAck = (token: string) => publicRequest<IaAckView>(`/public/ia-ack/${encodeURIComponent(token)}`);
export const submitIaAck = (token: string, typed_name: string) =>
  publicRequest<{ ok: boolean; already?: boolean }>(`/public/ia-ack/${encodeURIComponent(token)}`, { method: "POST", json: { typed_name } });

// Portfolio Records: factual records and atomic, revision-checked imports.
export interface ProductCapabilities {
  bookkeeping_only: boolean;
  professional_records?: boolean;
  product_mode: "portfolio_records" | "research";
}
export async function getProductCapabilities(): Promise<ProductCapabilities> {
  const res = await fetch(`${BASE_URL}/health`);
  if (!res.ok) throw new Error("Could not load product configuration");
  const data = await res.json();
  return { bookkeeping_only: !(data.bookkeeping_only === false && data.product_mode === "research"),
    professional_records: data.professional_records === true,
    product_mode: data.product_mode === "research" && data.bookkeeping_only === false ? "research" : "portfolio_records" };
}
export interface RecordsSnapshot {
  holdings: Holding[];
  mf_holdings: MFHolding[];
  transactions: Txn[];
  revision: string;
  reconciliation: {
    issues: { code: string; asset?: string; key?: string; message: string }[];
    counts: { equity: number; funds: number; transactions: number };
    complete: boolean;
  };
  history: { id: string; created_at: string; source?: string; counts?: Record<string, number>; action?: string;
    ledger_replaced?: boolean; resolved_prior_notes?: boolean; resolution_note?: string }[];
}
export interface RecordsImportPayload {
  expected_revision: string;
  holdings?: Holding[];
  mf_holdings?: MFHolding[];
  transactions?: Txn[];
  mode: "merge" | "replace";
  confirm_drop?: boolean;
  replace_transactions?: boolean;
  confirm_ledger_replace?: boolean;
  resolve_notes?: boolean;
  resolution_note?: string;
  source?: string;
  notes?: string[];
}
function recordsPath(clientId?: string) {
  return clientId ? `/clients/${encodeURIComponent(clientId)}/records` : "/portfolio/records";
}
export function getRecords(clientId?: string): Promise<RecordsSnapshot> {
  return request<RecordsSnapshot>(recordsPath(clientId));
}
export function importRecords(payload: RecordsImportPayload, clientId?: string): Promise<RecordsSnapshot> {
  return request<RecordsSnapshot>(recordsPath(clientId) + "/import", { method: "POST", json: payload });
}
export function restoreRecords(expected_revision: string, import_id: string, clientId?: string): Promise<RecordsSnapshot> {
  return request<RecordsSnapshot>(recordsPath(clientId) + "/restore",
    { method: "POST", json: { expected_revision, import_id } });
}
export async function exportRecords(clientId?: string): Promise<Blob> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${BASE_URL}${recordsPath(clientId)}/export`,
    { headers: { Authorization: `Bearer ${token}`, ...await appCheckHeader() } });
  if (!res.ok) throw new Error("Could not export records");
  return res.blob();
}

export interface RecordsPlan {
  sku: "practice" | "growth"; name: string; amount: number; currency: string;
  max_clients: number; days: number; auto_renew: false;
}
export interface RecordsPlans {
  plans: RecordsPlan[];
  current: { band?: string; status?: string; expires_at?: string; paid_through?: string; order_id?: string;
    queued_terms?: { band?: string; starts_at?: string; expires_at?: string }[] } | null;
  configured: boolean;
}
export function getRecordsPlans(): Promise<RecordsPlans> {
  return request<RecordsPlans>("/billing/records-plans");
}
export function createRecordsOrder(plan: "practice" | "growth"): Promise<{
  order_id: string; amount: number; currency: string; key_id: string; plan: RecordsPlan;
}> {
  return request("/billing/records-order", { method: "POST", json: { plan } });
}

export interface RecordsSummary {
  equity?: { ticker: string; qty: number; cmp: number | null; current_value: number | null; pnl_value: number | null; pnl_pct: number | null; day_change_pct: number | null }[];
  funds?: { scheme_code: string; scheme_name?: string | null; folio?: string; units: number; nav: number | null; nav_date?: string; current_value: number | null; invested?: number | null; pnl: number | null; pnl_pct: number | null; day_change_pct?: number | null }[];
  generated_at: string;
  equity_as_of?: string | null;
  funds_as_of?: string | null;
  totals: { current_value: number | null; invested: number | null; pnl_value: number | null; pnl_pct: number | null; n_positions: number; n_priced: number; n_costed_priced: number };
  performance: ClientPerformance & { return_available: boolean };
  warnings: string[];
}
export function getRecordsSummary(clientId?: string): Promise<RecordsSummary> {
  return request<RecordsSummary>(recordsPath(clientId) + "/summary");
}


// ── Company Dossier + the saved-research library (2026-10-12) ─────────────────
export interface DossierStart {
  job_id: string; status: string; credits_charged?: number; duplicate?: boolean; budget?: AiBudget;
}
export interface DossierStatus {
  job_id: string;
  status: "queued" | "running" | "complete" | "failed";
  progress?: { stage: string; step: number; of: number } | null;
  library_id?: string | null;
  error?: string | null;
}
export interface LibraryItem {
  id: string; kind: "deep_dive" | "dossier"; ticker: string; name?: string | null;
  created_at?: string | null; expires_at?: string | null; credits?: number; summary?: string | null;
}
export interface DossierFyRow {
  fy: string; revenue_cr?: number | null; revenue_growth_pct?: number | null; ebitda_margin_pct?: number | null;
  net_profit_cr?: number | null; profit_growth_pct?: number | null; roe_pct?: number | null; roce_pct?: number | null;
  debt_to_equity?: number | null; dividend_yield_pct?: number | null;
  /** Deterministic: profit broke from revenue this year ("… see note"). */
  flag?: string | null;
}
export interface DossierQuarter {
  quarter?: string | null; quarter_end?: string | null; result_date?: string | null;
  revenue_cr?: number | null; revenue_yoy_pct?: number | null; revenue_qoq_pct?: number | null;
  net_profit_cr?: number | null; profit_yoy_pct?: number | null; profit_qoq_pct?: number | null;
  ebitda_cr?: number | null; ebitda_margin_pct?: number | null; vs_estimate?: string | null;
  guidance?: string | null; one_time_items?: string | null;
}
export interface DossierPriceBehaviour {
  available: boolean; as_of?: string | null; last?: number; week52_high?: number; week52_low?: number;
  drawdown_from_high_pct?: number | null; above_low_pct?: number | null;
  returns_pct?: Record<string, number | null>; vs_dma_pct?: Record<string, number | null>;
  relative_to_nifty50_pct?: Record<string, number | null>;
}
export interface DossierPayload {
  kind: "dossier"; ticker: string; name: string; as_of?: string;
  company?: StockSnapshot["company"] | null; ratios?: StockSnapshot["ratios"] | null; peers?: StockSnapshotPeer[];
  five_year: { rows: DossierFyRow[]; fy_from?: string | null; fy_to?: string | null; revenue_cagr_pct?: number | null; pat_cagr_pct?: number | null };
  quarters: DossierQuarter[];
  price_behaviour: DossierPriceBehaviour;
  corporate_actions: { ex_date: string; ratio: number }[];
  /** Cash dividends per share, ascending by ex-date (last three years). */
  dividends?: { ex_date: string; amount: number }[];
  next_earnings?: string | null;
  /** Adviser Dossier only (tier "pro"). */
  tier?: "standard" | "pro";
  prepared_for?: string | null;
  cashflow?: { fy: string; period_end?: string; cfo_cr?: number | null; capex_cr?: number | null; fcf_cr?: number | null; cfo_to_pat?: number | null }[];
  balance?: { fy: string; period_end?: string; cash_cr?: number | null; debt_cr?: number | null; net_debt_cr?: number | null; working_capital_cr?: number | null }[];
  pe_band?: { available: boolean; min?: number; median?: number; max?: number; current?: number; position_pct?: number; from?: string; to?: string; days?: number };
  implied?: Record<string, unknown> | null;
  shareholding?: { quarter: string; promoter_pct?: string; fii_pct?: string; dii_pct?: string; public_pct?: string; pledged_pct?: string }[];
  disclosed_risks?: string[];
  research?: DeepDiveResearch | null; narrative?: string | null;
  /** One narration, titled: Multi-year numbers / Latest quarter / Price and averages / Market ratios / Listed peers. */
  narrative_sections?: { title: string; body: string }[];
  exceptional_items?: { period?: string; item: string; amount?: string }[];
  /** Pre-2026-10-12 saved dossiers only. */
  numbers_narrative?: string | null;
  research_cached?: boolean; disclaimer: string;
}
export interface LibraryDoc extends LibraryItem {
  payload: DeepDiveResult | DossierPayload;
}

export function startDossier(ticker: string, name: string, position?: Record<string, unknown> | null,
                             opts: { tier?: "standard" | "pro"; prepared_for?: string | null } = {}): Promise<DossierStart> {
  return request<DossierStart>("/dossier/start", { method: "POST", json: { ticker, name, position, tier: opts.tier ?? "standard", prepared_for: opts.prepared_for ?? null } });
}
export function dossierStatus(jobId: string): Promise<DossierStatus> {
  return request<DossierStatus>(`/dossier/status/${encodeURIComponent(jobId)}`);
}
export function libraryList(limit = 50): Promise<{ items: LibraryItem[] }> {
  return request<{ items: LibraryItem[] }>(`/library?limit=${limit}`);
}
export function libraryGet(id: string): Promise<LibraryDoc> {
  return request<LibraryDoc>(`/library/${encodeURIComponent(id)}`);
}
export function libraryDelete(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/library/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function libraryLatest(kind: LibraryItem["kind"], ticker: string): Promise<{ item: LibraryItem | null }> {
  return request<{ item: LibraryItem | null }>(`/library/latest?kind=${kind}&ticker=${encodeURIComponent(ticker)}`);
}
